import BrandLogo from '../components/BrandLogo';
import WelcomeTour from '../components/WelcomeTour';
import GiftCouponCard from '../components/GiftCouponCard';
import LauraRecommendsToast from '../components/LauraRecommendsToast';
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Briefcase, Activity, Sparkles, Settings, LogOut, Calendar as CalendarIcon, HelpCircle, X } from "lucide-react";
import Onboarding from "../components/Onboarding";
import SEOHead from "../components/SEOHead";
import NumberModal from "../components/NumberModal";
import MobileCalendar from "../components/MobileCalendar";
import CompactMobileCalendar from "../components/CompactMobileCalendar";
import ImportantDaysExplanation from "../components/ImportantDaysExplanation";
import CalendarExplanation from "../components/CalendarExplanation";
import LifeStagesCard from "../components/LifeStagesCard";
import PinnacleCard from "../components/PinnacleCard";
import AgendaDownloadSection from "../components/AgendaDownloadSection";
import SynastrySection from "../components/SynastrySection";
import HorizontalNumerologicaSection from "../components/HorizontalNumerologicaSection";
import PricingPlans from "../components/PricingPlans";
import TrialBanner from "../components/TrialBanner";
import { profileFailureRoute } from "../services/http";
import { trackActivity } from "../services/activity";
import SubscriptionManager from "../components/SubscriptionManager";
import ProfileSelector from "../components/ProfileSelector";
import NinjaPurchaseModal from "../components/NinjaPurchaseModal";
import ActivitySuggestions from "../components/ActivitySuggestions";
import { 
  generatePersonalizedMessage, 
  generateBatchMessages, 
  listNinjaProfiles, 
  createNinjaProfile, 
  activateNinjaProfile, 
  updateNinjaProfile, 
  deleteNinjaProfile,
  fetchOrCreatePinnacle
} from "../services/api";
import { isImportantDay, getImportantDayInfo, getImportantDayColor, getImportantDayTextColor, IMPORTANT_DAYS } from "../utils/importantDays";
import { authManager, isAuthenticated, getCurrentUser } from "../services/authManager";
import { getOrCreateProfileId, getOrCreateNinjaProfileId } from "../utils/uuid";
import { getPlanDateLimits, getNinjaLimit, formatRangeForHumans, formatCurrency } from "../utils/planCatalog";
import { exportToCalendar } from "../utils/calendarExport";

// ===== Utilidades Numerología (reducción respetando 11 y 22) =====
const sumDigits = (n) => Math.abs(n).toString().split("").reduce((a, b) => a + Number(b), 0);
const reduceNum = (n) => { let x = Math.abs(n); while (x >= 10 && x !== 11 && x !== 22) x = sumDigits(x); return x; };
// Reducir SIN respetar números maestros (para semana universal)
const reduceNumCustom = (n) => { let x = Math.abs(n); while (x > 9) x = sumDigits(x); return x; };

const universalYear = (Y) => reduceNum(Y);
const universalMonth = (M, Y) => reduceNum(universalYear(Y) + M);
const universalDay = (D, M, Y) => reduceNum(universalYear(Y) + M + D);
const personalYear = ({ d, m }, Y) => reduceNum(Y + m + d);
const personalMonth = ({ d, m }, date) => {
  const PY = personalYear({ d, m }, date.getFullYear());
  const M = date.getMonth() + 1;
  return reduceNum(PY + M);
};
const personalDay = ({ d, m }, date) => {
  const PY = personalYear({ d, m }, date.getFullYear());
  const M = date.getMonth() + 1;
  const D = date.getDate();
  return reduceNum(PY + D + M);
};

// Índice de semana basado solo en el día del mes (1-7, 8-14, 15-21, 22-31)
const weekIndex = (date) => {
  const day = date.getDate();
  if (day <= 7) return 1;
  if (day <= 14) return 2;
  if (day <= 21) return 3;
  return 4;
};

// Semana Universal: Fórmula encadenada según número de semana del mes (1-4)
// Semana 1 = reduce(reduce(Mes) + reduce(Año))
// Semana 2 = reduce(reduce(Año) + Semana1)
// Semana 3 = reduce(Semana1 + Semana2)
// Semana 4 = reduce(reduce(Mes) + Semana1)
const universalWeek = (date) => {
  const M = date.getMonth() + 1;
  const Y = date.getFullYear();
  const week = weekIndex(date); // 1, 2, 3 o 4
  
  const reducedMonth = reduceNum(M);
  const reducedYear = reduceNum(Y);
  
  const weekOne = reduceNum(reducedMonth + reducedYear);
  if (week === 1) return weekOne;
  
  const weekTwo = reduceNum(reducedYear + weekOne);
  if (week === 2) return weekTwo;
  
  const weekThree = reduceNum(weekOne + weekTwo);
  if (week === 3) return weekThree;
  
  const weekFour = reduceNum(reducedMonth + weekOne);
  return weekFour;
};

// Semana Personal: según código oficial Person.js (líneas 246-276)
const personalWeek = ({ d, m }, date) => {
  const PY = personalYear({ d, m }, date.getFullYear());
  const currentMonth = date.getMonth() + 1;
  const day = date.getDate();
  
  // Semana 1 base
  const weekOne = reduceNum(PY + currentMonth);
  
  if (day >= 1 && day <= 7) {
    return weekOne; // Semana 1
  }
  
  if (day >= 8 && day <= 14) {
    return reduceNum(PY + weekOne); // Semana 2
  }
  
  const weekTwo = reduceNum(PY + weekOne);
  if (day >= 15 && day <= 21) {
    return reduceNum(weekTwo + weekOne); // Semana 3
  }
  
  // Día 22-31
  return reduceNum(currentMonth + weekOne); // Semana 4
};

const WEEK_TALENTS = {
  1: "Liderazgo e independencia",
  2: "Conexión y unión",
  3: "Expresión y comunicación",
  4: "Acción y voluntad",
  5: "Adaptabilidad y agilidad mental",
  6: "Apoyo y solidaridad",
  7: "Profundidad y compromiso",
  8: "Fuerza y poder personal",
  9: "Notoriedad y creatividad",
  11: "Realización e inspiración del grupo",
  22: "Superación de grandes obstáculos y progreso",
};
const WEEK_INDEX_TO_TALENT = { 1: 1, 2: 2, 3: 3, 4: 4 };

// ===== Utilidades para el calendario =====
/**
 * Formatea una fecha a formato ISO (YYYY-MM-DD)
 * @param {Date} date - Fecha a formatear
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
const formatISODate = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().split('T')[0];
};

/**
 * Ajusta una fecha al rango permitido del plan
 * @param {Date} date - Fecha a ajustar
 * @param {Object} range - Rango con {start, end}
 * @returns {Date} Fecha ajustada dentro del rango
 */
const clampDateToRange = (date, range) => {
  if (!range) return date;
  const { start, end } = range;
  if (start && date < start) return start;
  if (end && date > end) return end;
  return date;
};

const NUM_KEYWORDS = {
  1: { k: "liderazgo e independencia", caution: "postergar o ceder tu decisión", verbs: { work: "inicia", love: "da el primer paso", health: "activa tu cuerpo", spirit: "declara tu intención" } },
  2: { k: "colaboración y escucha", caution: "hacerlo en soledad", verbs: { work: "coopera", love: "conversa con empatía", health: "hidrátate y descansa", spirit: "agradece y conecta" } },
  3: { k: "expresión y creatividad", caution: "guardarte tus ideas", verbs: { work: "presenta", love: "expresa afecto", health: "respira profundo", spirit: "escribe 3 ideas" } },
  4: { k: "orden y ejecución", caution: "improvisar", verbs: { work: "ejecuta", love: "acuerda reglas claras", health: "organiza tu rutina", spirit: "ordena tu espacio" } },
  5: { k: "cambio y exploración", caution: "apegarte a un plan", verbs: { work: "prueba algo nuevo", love: "sorprende", health: "muévete al aire libre", spirit: "visualiza caminos" } },
  6: { k: "cuidado y responsabilidad", caution: "cargar de más", verbs: { work: "fortalece alianzas", love: "nutre el vínculo", health: "aliméntate con cariño", spirit: "sirve desde el corazón" } },
  7: { k: "profundidad y estudio", caution: "dispersarte", verbs: { work: "investiga", love: "escucha en silencio", health: "descanso mental", spirit: "medita 10 minutos" } },
  8: { k: "poder y logros", caution: "controlar en exceso", verbs: { work: "negocia y decide", love: "pon límites sanos", health: "activa tu fuerza", spirit: "reafirma tu poder" } },
  9: { k: "cierre y proyección pública", caution: "dramatizar", verbs: { work: "cierra y muestra", love: "honra lo vivido", health: "depura y suelta", spirit: "agradece y libera" } },
  11: { k: "inspiración y visión", caution: "dudar de tu intuición", verbs: { work: "inspira al equipo", love: "eleva la conversación", health: "respira consciente", spirit: "canaliza una idea" } },
  22: { k: "progreso y construcción mayor", caution: "subestimarte", verbs: { work: "planifica en grande", love: "sueñen a futuro", health: "disciplina suave", spirit: "ancla tu propósito" } },
};

// Detalle de la Energía del Día para días normales (1-9); el 11 y el 22 usan los textos de Día Maestro
const DAY_ENERGY_DETAIL = {
  1: "Es un día de comienzos: lo que arranques hoy toma impulso propio. Da el primer paso en eso que vienes aplazando y decide sin esperar la aprobación de otros.",
  2: "Hoy avanzas mejor en compañía que por tu cuenta. Pide apoyo, escucha antes de responder y busca acuerdos: el tacto y la paciencia rinden más que la prisa.",
  3: "Tu palabra tiene hoy más alcance que de costumbre. Presenta esa propuesta, comparte lo que piensas o retoma un proyecto creativo; también es un buen día para vender y conectar con clientes.",
  4: "Es un día para construir paso a paso. Haz una lista corta, termina lo que está a medias y ordena tus tiempos: lo que estructures hoy te ahorrará trabajo el resto de la semana.",
  5: "La energía de hoy se mueve rápido y premia la flexibilidad. Cambia la ruta, prueba otra forma de hacer una tarea o di que sí a una invitación inesperada.",
  6: "La atención está en tu hogar, tu familia y quienes cuentan contigo. Cumple tus compromisos y ofrece ayuda donde haga falta, pero reparte responsabilidades: cuidar también incluye cuidarte.",
  7: "Es un día para ir hacia adentro. Reserva un rato sin interrupciones para estudiar, investigar o pensar a fondo una decisión: hoy pesa más la calidad de tu atención que la cantidad de tareas.",
  8: "La energía favorece los resultados concretos. Negocia, pide lo que vale tu trabajo y decide con autoridad: es un buen día para avanzar en tus metas profesionales y económicas.",
  9: "Es un día para cerrar ciclos y soltar lo que ya cumplió su función. Termina pendientes, entrega lo que tienes listo y muestra tu trabajo: lo que concluyas hoy gana visibilidad.",
};

const SCOPE_LABEL = { day: "Diario", week: "Semanal", month: "Mensual" };

const HeadChip = ({ label, value, desc, tooltipType }) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        title="Haz clic para más información"
        aria-label={`${label}: más información`}
        className="w-full h-full aspect-square p-2 md:p-3 rounded-lg md:rounded-xl bg-morado/10 border border-morado/20 flex flex-col items-center justify-center text-center cursor-pointer hover:border-morado/40 hover:shadow-md transition"
      >
        <div className="w-11 h-11 md:w-14 md:h-14 rounded-full flex items-center justify-center font-bold text-morado bg-white border-2 border-[#d4af37] mb-2 md:mb-3 text-xs md:text-base leading-tight">
          {value}
        </div>
        <div className="space-y-0.5 md:space-y-1">
          <div className="flex items-center justify-center gap-1 text-xs uppercase tracking-wide text-morado font-medium leading-tight">
            <span>{label}</span>
            <HelpCircle size={14} className="shrink-0" aria-hidden="true" />
          </div>
          <div className="text-xs text-gray-700 leading-tight">
            {desc}
          </div>
        </div>
      </button>
      <NumberModal number={value} type={tooltipType} isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};

const AreaCard = ({ title, icon: Icon, gradient, message, caution, isLoading, pd, ud }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="rounded-2xl shadow-xl border overflow-hidden" style={{ borderColor: "#d4af37" }}>
    <div className="p-4 md:p-5 text-white" style={{ background: gradient }}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-xl bg-white/15"><Icon size={22} /></div>
          <h4 className="text-lg md:text-xl font-semibold">{title}</h4>
        </div>
        {pd != null && ud != null && (
          <div
            className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold bg-white/20 border-2 border-white/40 text-sm"
            title="Energía del día (personal / universal)"
            aria-label={`Energía del día: ${pd} personal, ${ud} universal`}
          >
            {pd}/{ud}
          </div>
        )}
      </div>
    </div>
    <div className="p-4 md:p-5 bg-white relative min-h-[120px]">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-3"
          >
            <div className="w-12 h-12 rounded-full border-4 border-[#d4af37] border-t-morado" 
                 style={{ 
                   background: "var(--color-morado)",
                   opacity: 0.2
                 }} />
            <p className="text-sm text-morado font-medium">Actualizando mensaje...</p>
          </motion.div>
        </div>
      ) : (
        <>
          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{message}</p>
          {caution && <p className="mt-3 text-sm text-gray-600"><span className="font-medium text-morado">⚠️ Evita:</span> {caution}</p>}
        </>
      )}
    </div>
  </motion.div>
);

const Segmented = ({ value, onChange }) => (
  <div className="inline-flex rounded-xl md:rounded-2xl p-0.5 md:p-1 bg-white border" style={{ borderColor: "#d4af37" }}>
    {["day", "week", "month"].map((k) => (
      <button 
        key={k} 
        onClick={() => onChange(k)} 
        className={`
          px-2 md:px-3 lg:px-4 py-1 md:py-1.5 rounded-lg md:rounded-xl 
          text-xs md:text-sm transition 
          ${value === k ? "text-white" : "text-morado"}
        `} 
        style={{ 
          background: value === k ? "var(--color-morado)" : "transparent" 
        }}
      >
        {SCOPE_LABEL[k]}
      </button>
    ))}
  </div>
);

// ===== Stickers (sellos) y calendario =====
const STICKER_PRIORITY = ["master22", "master11", "power", "sync"];
const STICKER_META = {
  master11: { label: "Inspiración", emoji: "💡11" },
  master22: { label: "Progreso", emoji: "🚀22" },
  power: { label: "Poder", emoji: "🔥" },
  sync: { label: "Sincronía", emoji: "✨" },
};
function isWeekChange(day) { return day === 1 || day === 8 || day === 15 || day === 22; }
function computeSpecials(dob, date) {
  const pd = personalDay(dob, date);
  const ud = universalDay(date.getDate(), date.getMonth() + 1, date.getFullYear());
  const pm = personalMonth(dob, date);
  const tags = [];
  if (pd === 11) tags.push("master11");
  if (pd === 22) tags.push("master22");
  // Removido: if (pd === pm) tags.push("power"); // Quitamos los íconos de fuego
  if (pd === ud) tags.push("sync");
  const ordered = tags.sort((a, b) => STICKER_PRIORITY.indexOf(a) - STICKER_PRIORITY.indexOf(b));
  return ordered.slice(0, 2);
}
const BonusCard = ({ specials, pd, pm, um, ud }) => {
  // Determinar si hay un día especial
  const hasSpecial = specials && specials.length > 0;
  const primary = hasSpecial ? specials[0] : null;
  const meta = primary ? STICKER_META[primary] : null;
  
  let text = "";
  let explanation = "";
  let caution = "";
  let emoji = "✨";
  
  if (primary === "master11") {
    text = `Día Maestro 11: baja una idea a la Tierra. ✍️ Escribe 1 inspiración y da un micropaso hoy.`;
    explanation = "Los días 11 son de inspiración especial. Es momento de conectar con ideas elevadas y llevarlas a la práctica.";
    emoji = meta.emoji;
  } else if (primary === "master22") {
    text = `Día Maestro 22: construye en grande. 🧱 Define 1 hito con fecha y el siguiente paso de 10–15 min.`;
    explanation = "Los días 22 son de construcción mayor. Es tiempo de pensar en grande y dar pasos concretos hacia metas importantes.";
    emoji = meta.emoji;
  } else if (primary === "power") {
    text = `Día de Poder: tu Día (${pd}) y tu Mes (${pm}) alinean esfuerzos. 🔥 Elige una acción que acerque visiblemente tu objetivo.`;
    explanation = "Cuando tu día personal y mes personal coinciden, hay una alineación especial de energías. Es momento de acción decisiva.";
    emoji = meta.emoji;
  } else if (primary === "sync") {
    text = `Sincronía: tu Día personal y el Universal coinciden. ✨ Observa una señal, agradece y actúa en consecuencia.`;
    explanation = "Tu energía personal y la energía universal están en sintonía. Es un día de señales y sincronías especiales.";
    emoji = meta.emoji;
  } else {
    // Día normal - mostrar información general del día personal
    const numInfo = NUM_KEYWORDS[pd] || NUM_KEYWORDS[1];
    const detail = DAY_ENERGY_DETAIL[pd] || DAY_ENERGY_DETAIL[1];
    // Todo el mensaje va como título; la advertencia se muestra aparte con el mismo formato que las categorías
    text = `Hoy tu energía personal vibra en ${pd}: ${numInfo.k}. Aprovecha la energía del número ${pd} para avanzar en tus objetivos. ${detail}`;
    caution = numInfo.caution;
  }
  
  return (
    <motion.div id="energia-del-dia" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border shadow-lg overflow-hidden" style={{ borderColor: "#d4af37" }}>
      <div className="px-4 py-3 text-white" style={{ background: "var(--color-morado)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">{emoji}</span>
            <div>
              <h4 className="font-semibold">Energía del Día</h4>
              <p className="text-xs opacity-90">Tu vibración personal de hoy</p>
            </div>
          </div>
          <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold bg-white/20 border-2 border-white/40 text-sm">
            {pd}/{ud}
          </div>
        </div>
      </div>
      <div className="p-4 bg-white text-gray-800">
        <p className={`leading-relaxed ${caution ? "font-semibold" : "font-medium mb-2"}`}>{text}</p>
        {explanation && <p className="text-sm text-gray-600 leading-relaxed">{explanation}</p>}
        {caution && <p className="mt-3 text-sm text-gray-600"><span className="font-medium text-morado">⚠️ Evita:</span> {caution}.</p>}
      </div>
    </motion.div>
  );
};
function buildMonthMatrix(baseDate) {
  const Y = baseDate.getFullYear();
  const M = baseDate.getMonth();
  const first = new Date(Y, M, 1);
  // Ajustar para que Lunes sea 0, Domingo sea 6
  let startDow = first.getDay() - 1;
  if (startDow < 0) startDow = 6; // Si es Domingo, convertirlo a 6
  
  const daysInMonth = new Date(Y, M + 1, 0).getDate();
  const prevMonthDays = new Date(Y, M, 0).getDate();
  const cells = [];
  
  for (let i = 0; i < startDow; i++) {
    const day = prevMonthDays - startDow + 1 + i;
    cells.push({ date: new Date(Y, M - 1, day), inMonth: false });
  }
  
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(Y, M, d), inMonth: true });
  }
  
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, inMonth: next.getMonth() === M });
  }
  
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, inMonth: next.getMonth() === M });
  }
  
  return cells;
}
const DaySticker = ({ tags, pd }) => { 
  const t = tags?.[0]; 
  const meta = t ? STICKER_META[t] : null;
  
  // Solo mostrar emoji si es un día especial (sticker) o día importante
  const shouldShowEmoji = meta || isImportantDay(pd);
  
  if (!shouldShowEmoji) return null;
  
  const emoji = meta?.emoji || getImportantDayInfo(pd)?.emoji;
  
  return (
    <div 
      title={meta?.label || getImportantDayInfo(pd)?.label || `Día Personal ${pd}`} 
      className="absolute inset-0 flex items-center justify-center text-sm sm:text-base md:text-lg lg:text-xl select-none pointer-events-none"
    >
      {emoji}
    </div>
  ); 
};

const createMainProfile = () => ({
  id: 'main',
  name: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  birth: '',
  occupation: [],
  status: '',
  isNinja: false
});

const CalendarMonth = ({ dob, currentDate, selectedISO, onPick, isDateAllowed, onPrevMonth, onNextMonth, canPrevMonth, canNextMonth }) => {
  const matrix = React.useMemo(() => buildMonthMatrix(currentDate), [currentDate]);
  const pm = personalMonth(dob, currentDate);

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border" style={{ borderColor: "#d4af37" }}>
      {/* Header responsive con navegación */}
      <div className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
        {/* Botón mes anterior */}
        <button
          onClick={onPrevMonth}
          disabled={!canPrevMonth}
          className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
            canPrevMonth
              ? 'text-morado hover:bg-morado/10 cursor-pointer'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title={canPrevMonth ? "Ir al mes anterior" : "No puedes retroceder antes del inicio de tu plan"}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden lg:inline text-sm font-medium">Mes anterior</span>
        </button>
        
        {/* Título del mes */}
        <div className="flex-1 text-center">
          <div className="text-morado font-semibold text-sm md:text-base">
            {currentDate.toLocaleDateString('es-ES', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </div>
          <div className="text-xs text-gray-600 hidden sm:block mt-1">
            Leyenda: 🌟3 • 💰8 • 💡11 • 🚀22
          </div>
        </div>
        
        {/* Botón mes siguiente */}
        <button
          onClick={onNextMonth}
          disabled={!canNextMonth}
          className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
            canNextMonth
              ? 'text-morado hover:bg-morado/10 cursor-pointer'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title={canNextMonth ? "Ir al mes siguiente" : "No puedes avanzar más allá del límite de tu plan"}
        >
          <span className="hidden lg:inline text-sm font-medium">Mes siguiente</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      {/* Calendar grid */}
      <div className="px-1 sm:px-2 md:px-3 pb-3">
        {/* Days of week */}
        <div className="grid grid-cols-7 text-center text-xs font-medium text-morado mb-1.5">
          {['L','M','M','J','V','S','D'].map((d, index) => (
            <div key={index} className="py-1.5 px-1">{d}</div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 md:gap-2">
          {matrix.map((cell, idx) => {
            const d = cell.date;
            const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            const pd = personalDay(dob, d);
            const ud = universalDay(d.getDate(), d.getMonth() + 1, d.getFullYear());
            const specials = computeSpecials(dob, d);
            const isSelected = iso === selectedISO;
            const inMonth = cell.inMonth;
            
            // Detectar si es HOY
            const today = new Date();
            const isToday = d.getDate() === today.getDate() && 
                           d.getMonth() === today.getMonth() && 
                           d.getFullYear() === today.getFullYear();
            
            const isAllowed = isDateAllowed ? isDateAllowed(iso) : true;
            const isDisabled = !inMonth || !isAllowed;
            const baseClass = inMonth ? "bg-white" : "bg-gray-50 opacity-60";

            const buttonStateClass = isDisabled
              ? 'bg-gray-200 opacity-40 cursor-not-allowed'
              : isToday 
                ? 'border-4 border-morado shadow-lg shadow-morado/20 bg-morado/5 ring-2 ring-morado/30 ring-offset-2 animate-pulse'
                : `border ${isSelected ? 'ring-2 ring-[#d4af37] border-[#d4af37]' : ''} ${!isImportantDay(pd) ? 'border-gray-200' : ''} ${isImportantDay(pd) ? getImportantDayColor(pd) : baseClass}`;

            const hoverClass = !isDisabled ? 'hover:bg-gray-50' : 'cursor-default';
            
            return (
              <button 
                key={idx}
                disabled={isDisabled}
                onClick={() => !isDisabled && onPick(iso)} 
                className={`
                  relative rounded-lg md:rounded-xl p-1.5 sm:p-2 
                  min-h-[3.5rem] sm:min-h-[4rem] md:min-h-[4.5rem] lg:min-h-[5rem]
                  text-left transition-all hover:shadow 
                  ${buttonStateClass}
                  ${hoverClass}
                `}
              >
                {/* Indicador HOY */}
                {isToday && !isDisabled && (
                  <div className="absolute top-0 right-0 bg-morado text-white text-[0.5rem] sm:text-[0.6rem] font-bold px-1 sm:px-1.5 py-0.5 rounded-bl-lg rounded-tr-lg">
                    HOY
                  </div>
                )}
                
                <DaySticker tags={specials} pd={pd} />
                
                {/* Date in top-left corner */}
                <div className="absolute top-1 left-1">
                  <span className={`text-xs sm:text-sm font-medium ${isToday && !isDisabled ? 'text-morado font-bold' : 'text-gray-500'}`}>
                    {d.getDate()}
                  </span>
                </div>
                
                {/* Numerological binomial - centered and larger when no emoji */}
                {(!specials || specials.length === 0) && !isImportantDay(pd) ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span 
                      className="inline-flex items-center justify-center text-xs sm:text-sm md:text-base font-bold px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-full border" 
                      style={{ 
                        borderColor: '#d4af37', 
                        color: 'var(--color-morado)', 
                        background: '#fff7ed' 
                      }}
                    >
                      {pd}/{ud}
                    </span>
                  </div>
                ) : (
                  <div className="absolute bottom-1 right-1">
                    <span 
                      className="inline-flex items-center justify-center text-xs font-bold px-1 sm:px-1.5 py-0.5 rounded-full border" 
                      style={{ 
                        borderColor: '#d4af37', 
                        color: 'var(--color-morado)', 
                        background: '#fff7ed' 
                      }}
                    >
                      {pd}/{ud}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const navigate = useNavigate();
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 ESTADOS DE INICIALIZACIÓN (CRÍTICOS - Previenen race conditions)
  // ═══════════════════════════════════════════════════════════════════════════
  const [isAppReady, setIsAppReady] = useState(false);
  const [initError, setInitError] = useState(null);
  const [initAttempt, setInitAttempt] = useState(0);
  const [initLoadingMessage, setInitLoadingMessage] = useState('Cargando tu experiencia...');
  const initAbortControllerRef = useRef(null);
  
  // Estados para la aplicación
  const [trialInfo, setTrialInfo] = useState(null);
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  
  // Estados para exportación de calendario
  const [showCalendarHelpModal, setShowCalendarHelpModal] = useState(false);
  const [exportScope, setExportScope] = useState('month'); // 'month' | 'year'
  const [exportYear, setExportYear] = useState(() => new Date().getFullYear());
  const [profiles, setProfiles] = useState(() => {
    const fallback = [createMainProfile()];
    if (typeof window === 'undefined') {
      return fallback;
    }
    try {
      const saved = window.localStorage.getItem('numerologiaProfiles');
      
      if (saved) {
        const parsed = JSON.parse(saved);
        
        if (Array.isArray(parsed) && parsed.length > 0) {
          const timestamp = Date.now();
          const sanitized = parsed.map((p, index) => {
            const occupation = Array.isArray(p.occupation)
              ? p.occupation
              : (p.occupation ? [p.occupation] : []);
            const derivedId = (typeof p.id === 'string' && p.id.trim() !== '')
              ? p.id.trim()
              : (p.isNinja ? `ninja-${timestamp}-${index}` : `profile-${timestamp}-${index}`);
            return {
              id: derivedId,
              name: p.name || '',
              apellidoPaterno: p.apellidoPaterno || p.apellido_paterno || '',
              apellidoMaterno: p.apellidoMaterno || p.apellido_materno || '',
              birth: p.birth || p.birth_date || '',
              occupation,
              status: p.status || '',
              isNinja: Boolean(p.isNinja)
            };
          });
          const mainIndex = sanitized.findIndex(profile => profile.id === 'main' || !profile.isNinja);
          
          if (mainIndex >= 0) {
            return sanitized.map((profile, idx) => (
              idx === mainIndex
                ? { ...profile, id: 'main', isNinja: false }
                : profile
            ));
          }
          return [createMainProfile(), ...sanitized];
        }
      }
    } catch (error) {
      console.error('❌ Error recuperando perfiles:', error);
    }
    return fallback;
  });
  const [userData, setUserData] = useState(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const saved = window.localStorage.getItem('numerologiaUserData');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('❌ Error recuperando userData:', error);
    }
    return null;
  });
  const [currentProfileId, setCurrentProfileId] = useState(() => {
    if (typeof window === 'undefined') {
      return 'main';
    }
    const saved = window.localStorage.getItem('numerologiaCurrentProfile');
    return saved || 'main';
  });
  const currentProfile = useMemo(() => {
    if (!profiles || profiles.length === 0) {
      return createMainProfile();
    }
    return profiles.find(profile => profile.id === currentProfileId) || profiles[0];
  }, [profiles, currentProfileId]);
  const [todayKey, setTodayKey] = useState(() => new Date().toISOString().slice(0, 10));
  const planRange = useMemo(() => {
    if (!subscriptionInfo) return null;
    return getPlanDateLimits(subscriptionInfo, new Date(todayKey));
  }, [subscriptionInfo, todayKey]);
  const planRangeSummary = useMemo(() => {
    if (!planRange) return '';
    const label = planRange.label ? planRange.label.charAt(0).toUpperCase() + planRange.label.slice(1) : '';
    const coverage = formatRangeForHumans(planRange.start, planRange.end);
    return coverage ? `${label} • ${coverage}` : label;
  }, [planRange]);

  const isDateWithinPlanRange = useCallback((value) => {
    if (!value) return false;
    const dateValue = value instanceof Date ? new Date(value) : new Date(`${value}T00:00:00`);
    if (Number.isNaN(dateValue.getTime())) return false;
    if (!planRange) return true;
    const { start, end } = planRange;
    if (start && dateValue < start) return false;
    if (end && dateValue > end) return false;
    return true;
  }, [planRange]);

  const ninjaCount = useMemo(() => profiles.filter(profile => profile.isNinja).length, [profiles]);

  const [maxNinjaProfiles, setMaxNinjaProfiles] = useState(1);
  const [ninjaLimits, setNinjaLimits] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const [date, setDate] = useState(() => {
    // Fecha del sistema por claridad en demo
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  });
  
  // Estado para el mes del calendario (navegación independiente)
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  
  // Flag para distinguir navegación manual de sincronización automática
  const isManualNavigationRef = useRef(false);
  
  // Sincronizar calendario con fecha seleccionada (solo cuando el usuario selecciona un día)
  // ⚠️ FIX: Removida dependencia de calendarMonth para evitar loops innecesarios
  useEffect(() => {
    // Si es navegación manual, no sincronizar
    if (isManualNavigationRef.current) {
      isManualNavigationRef.current = false;
      return;
    }
    
    const selectedDate = new Date(`${date}T00:00:00`);
    if (!Number.isNaN(selectedDate.getTime())) {
      // Usar callback para comparar con el valor actual sin agregarlo como dependencia
      setCalendarMonth(prevMonth => {
        if (selectedDate.getMonth() !== prevMonth.getMonth() || 
            selectedDate.getFullYear() !== prevMonth.getFullYear()) {
          return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        }
        return prevMonth;
      });
    }
  }, [date]); // ✅ Solo depende de date, no de calendarMonth
  
  // Funciones de navegación del calendario (DESPUÉS de declarar calendarMonth)
  const canNavigatePrevMonth = useCallback(() => {
    if (!planRange) return false;
    
    // Verificar si AL MENOS UN DÍA del mes anterior está dentro del rango
    const prevMonthYear = calendarMonth.getMonth() === 0 ? calendarMonth.getFullYear() - 1 : calendarMonth.getFullYear();
    const prevMonthMonth = calendarMonth.getMonth() === 0 ? 11 : calendarMonth.getMonth() - 1;
    
    // Verificar el primer día del mes anterior
    const firstDayOfPrevMonth = new Date(prevMonthYear, prevMonthMonth, 1);
    if (isDateWithinPlanRange(firstDayOfPrevMonth)) return true;
    
    // Verificar el último día del mes anterior
    const lastDayOfPrevMonth = new Date(prevMonthYear, prevMonthMonth + 1, 0);
    if (isDateWithinPlanRange(lastDayOfPrevMonth)) return true;
    
    // Si ni el primero ni el último están en el rango, el mes completo está fuera
    return false;
  }, [calendarMonth, planRange, isDateWithinPlanRange]);
  
  const canNavigateNextMonth = useCallback(() => {
    if (!planRange) return false;
    
    // Verificar si AL MENOS UN DÍA del mes siguiente está dentro del rango
    const nextMonthYear = calendarMonth.getMonth() === 11 ? calendarMonth.getFullYear() + 1 : calendarMonth.getFullYear();
    const nextMonthMonth = calendarMonth.getMonth() === 11 ? 0 : calendarMonth.getMonth() + 1;
    
    // Verificar el primer día del mes siguiente
    const firstDayOfNextMonth = new Date(nextMonthYear, nextMonthMonth, 1);
    if (isDateWithinPlanRange(firstDayOfNextMonth)) return true;
    
    // Verificar el último día del mes siguiente
    const lastDayOfNextMonth = new Date(nextMonthYear, nextMonthMonth + 1, 0);
    if (isDateWithinPlanRange(lastDayOfNextMonth)) return true;
    
    // Si ni el primero ni el último están en el rango, el mes completo está fuera
    return false;
  }, [calendarMonth, planRange, isDateWithinPlanRange]);
  
  const handlePrevMonth = useCallback(() => {
    if (canNavigatePrevMonth()) {
      trackActivity('calendar_month_changed', 'calendar');
      isManualNavigationRef.current = true;
      setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  }, [canNavigatePrevMonth]);
  
  const handleNextMonth = useCallback(() => {
    if (canNavigateNextMonth()) {
      trackActivity('calendar_month_changed', 'calendar');
      isManualNavigationRef.current = true;
      setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  }, [canNavigateNextMonth]);
  
  const [scope, setScope] = useState("day"); // day | week | month
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [welcomeTourRequest, setWelcomeTourRequest] = useState(0);
  const [currentView, setCurrentView] = useState("energia"); // energia | etapa | pinaculo | horizontal
  const [showBottomNav, setShowBottomNav] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastScrollYRef = useRef(0);
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      if (y > lastScrollYRef.current && y > 320) setShowBottomNav(true);
      else if (y < lastScrollYRef.current) setShowBottomNav(false);
      lastScrollYRef.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const [pinnacleState, setPinnacleState] = useState({
    loading: false,
    data: null,
    error: null,
    reason: null
  });
  const [pinnacleRefreshKey, setPinnacleRefreshKey] = useState(0);

  useEffect(() => {
    if (isAppReady && !showOnboarding) trackActivity('app_open', 'app');
  }, [isAppReady, showOnboarding]);
  useEffect(() => {
    if (!isAppReady || showOnboarding) return;
    const module = { energia: 'calendar', etapa: 'life_stage', pinaculo: 'pinnacle', horizontal: 'pinnacle', pareja: 'synastry', agenda: 'agenda' }[currentView];
    if (module) trackActivity('module_view', module);
  }, [isAppReady, currentView, showOnboarding]);
  const [isLoadingCards, setIsLoadingCards] = useState(false); // Estado para el loader de las cards
  
  // Estado para mensajes del backend (por categoría)
  const [backendMessages, setBackendMessages] = useState({
    work: null,
    love: null,
    wellness: null,
    spiritual: null,
    loading: false,
    error: null
  });

  // Estados para los datos del usuario
  const [name, setName] = useState('');
  const [apellidoPaterno, setApellidoPaterno] = useState('');
  const [apellidoMaterno, setApellidoMaterno] = useState('');
  const [birth, setBirth] = useState('');
  const [role, setRole] = useState([]);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      const key = new Date().toISOString().slice(0, 10);
      setTodayKey(prev => (prev === key ? prev : key));
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!profiles || profiles.length === 0) {
      const fallbackProfile = createMainProfile();
      const fallbackList = [fallbackProfile];
      setProfiles(fallbackList);
      setCurrentProfileId(fallbackProfile.id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('numerologiaProfiles', JSON.stringify(fallbackList));
        window.localStorage.setItem('numerologiaCurrentProfile', fallbackProfile.id);
      }
      return;
    }
    if (!profiles.some(profile => profile.id === currentProfileId)) {
      const fallback = profiles[0];
      if (fallback && fallback.id !== currentProfileId) {
        setCurrentProfileId(fallback.id);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('numerologiaCurrentProfile', fallback.id);
        }
      }
    }
  }, [profiles, currentProfileId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (userData) {
      window.localStorage.setItem('numerologiaUserData', JSON.stringify(userData));
    } else {
      window.localStorage.removeItem('numerologiaUserData');
    }
  }, [userData]);

  // Sincronizar estado con el perfil actual
  useEffect(() => {
    if (currentProfile) {
      setName(currentProfile.name);
      setApellidoPaterno(currentProfile.apellidoPaterno || '');
      setApellidoMaterno(currentProfile.apellidoMaterno || '');
      setBirth(currentProfile.birth);
      setRole(Array.isArray(currentProfile.occupation) ? currentProfile.occupation : [currentProfile.occupation || 'otro']);
      setStatus(currentProfile.status || 'soltero/a');
    }
  }, [currentProfile]);

  const isTrialAccount = getCurrentUser()?.isTrial === true;
  useEffect(() => {
    if (!isTrialAccount) return;
    let cancelled = false;
    let checking = false;
    const check = async () => {
      if (checking) return;
      checking = true;
      try {
        const { trial } = await authManager.getTrialStatus();
        if (cancelled) return;
        setTrialInfo(trial);
        if (trial?.status === 'expired') navigate('/trial/status', { replace: true });
        if (trial?.status === 'pending') navigate('/trial/start', { replace: true });
      } catch { /* Protected API requests still enforce the expiry if this check cannot connect. */ }
      finally { checking = false; }
    };
    check();
    const timer = window.setInterval(check, 60000);
    window.addEventListener('focus', check);
    return () => { cancelled = true; window.clearInterval(timer); window.removeEventListener('focus', check); };
  }, [isTrialAccount, navigate]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 INICIALIZACIÓN PRINCIPAL (Con AbortController para prevenir race conditions)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    console.log('🚀 [useEffect initializeApp] Ejecutando...');
    
    // Crear AbortController para cancelar si el componente se desmonta
    const abortController = new AbortController();
    initAbortControllerRef.current = abortController;
    setInitError(null);
    setIsAppReady(false);
    
    const initializeApp = async () => {
      // Helper para verificar si debemos abortar
      const shouldAbort = () => abortController.signal.aborted;
      
      try {
        setInitLoadingMessage('Verificando autenticación...');
        console.log('🔄 [initializeApp] Verificando autenticación...');
        const user = getCurrentUser();
        console.log('👤 [initializeApp] Usuario actual:', user);
        
        if (shouldAbort()) return;
        
        if (user) {
          console.log('✅ [initializeApp] Usuario encontrado, verificando pago...');
          
          // 🔄 POLLING: Si viene de PayPal, esperar a que el webhook procese
          const urlParams = new URLSearchParams(window.location.search);
          const fromPayPal = urlParams.get('subscription_id') || urlParams.get('token') || localStorage.getItem('pending_paypal_payment');
          
          if (fromPayPal) {
            console.log('💳 [PAYPAL_RETURN] Usuario viene de PayPal, esperando webhook...', { fromPayPal });
            localStorage.removeItem('pending_paypal_payment');
            setInitLoadingMessage('🎉 ¡Pago completado! Procesando tu suscripción...');
            
            // Polling: Intentar hasta 10 veces (20 segundos total)
            for (let attempt = 1; attempt <= 10; attempt++) {
              if (shouldAbort()) return;
              
              console.log(`🔄 [POLLING] Intento ${attempt}/10 - Verificando suscripción...`);
              
              try {
                const profileData = await authManager.getProfile({ signal: abortController.signal });
                if (profileData.subscription && profileData.subscription.planId) {
                  console.log(`✅ [POLLING] ¡Suscripción encontrada en intento ${attempt}!`);
                  setInitLoadingMessage('✅ ¡Suscripción activada! Cargando tu perfil...');
                  break;
                }
              } catch (err) {
                console.warn(`⚠️ [POLLING] Intento ${attempt} falló:`, err.message);
              }
              
              if (attempt < 10) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }
          }
          
          if (shouldAbort()) return;
          
          setInitLoadingMessage('Cargando tu información...');
          
          // 🚨 VERIFICAR PAGO ANTES DE TODO
          const profileData = await authManager.getProfile({ signal: abortController.signal });
          
          if (shouldAbort()) return;
          
          if (profileData.trial) {
            setTrialInfo(profileData.trial);
            if (profileData.trial.status === 'pending') { navigate('/trial/start', { replace: true }); return; }
            if (profileData.trial.status === 'expired') { navigate('/trial/status', { replace: true }); return; }
          }

          // 🔥 CASO 1: Usuario SIN suscripción
          if (!profileData.subscription) {
            console.warn('⚠️ [NO_SUBSCRIPTION] Usuario sin suscripción, redirigiendo a pricing...');
            navigate('/pricing');
            return;
          }
          
          // 🔥 CASO 2: Usuario CON suscripción pero pago pendiente
          const sub = profileData.subscription;
          if (sub.paymentStatus === 'pending' && !sub.paidAt && !sub.planId) {
            console.warn('⚠️ [PAYMENT_PENDING] Usuario con suscripción pendiente, redirigiendo...');
            navigate('/complete-purchase');
            return;
          }
          
          // 🔥 CASO 3: Usuario CON suscripción pero sin plan asignado
          if (!sub.planId) {
            console.warn('⚠️ [NO_PLAN] Usuario sin plan asignado, redirigiendo a pricing...');
            navigate('/pricing');
            return;
          }
          
          console.log('✅ [PAYMENT_VERIFIED] Usuario con pago completo:', {
            planId: sub.planId,
            displayName: sub.displayName
          });
          
          if (shouldAbort()) return;
          
          setInitLoadingMessage('Preparando tu calendario numerológico...');
          
          // Cargar perfil completo desde el backend
          const hasProfile = await loadUserProfile(profileData);
          
          if (shouldAbort()) return;
          
          console.log('📊 [initializeApp] hasProfile:', hasProfile);
          
          if (hasProfile) {
            console.log('✅ [initializeApp] Perfil completo, ocultando onboarding');
            setShowOnboarding(false);
          } else {
            console.log('⚠️ [initializeApp] Sin perfil completo, mostrando onboarding');
            setShowOnboarding(true);
          }
          
        } else {
          console.log('⚠️ [initializeApp] Sin usuario autenticado, buscando en localStorage');
          // Fallback para modo sin autenticación
          const savedUserData = localStorage.getItem('numerologiaUserData');
          if (savedUserData) {
            console.log('✅ [initializeApp] Datos encontrados en localStorage');
            const parsedData = JSON.parse(savedUserData);
            setUserData(parsedData);
            setName(parsedData.name);
            setApellidoPaterno(parsedData.apellidoPaterno || '');
            setApellidoMaterno(parsedData.apellidoMaterno || '');
            setBirth(parsedData.birth);
            setRole(Array.isArray(parsedData.occupation) ? parsedData.occupation : [parsedData.occupation]);
            setStatus(parsedData.status);
          }
        }
        
        // ✅ MARCAR COMO LISTO (solo si no abortamos)
        if (!shouldAbort()) {
          console.log('🎉 [initializeApp] ¡Inicialización completada!');
          setIsAppReady(true);
        }
        
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('❌ [initializeApp] Error:', error);
          setInitError(error.message || 'Error al cargar la aplicación');
          setInitLoadingMessage('No pudimos cargar tu calendario');
          const route = profileFailureRoute(error);
          if (route) navigate(route, { replace: true });
        }
      }
    };
    
    initializeApp();
    
    // Cleanup: Abortar si el componente se desmonta
    return () => {
      console.log('🧹 [initializeApp] Cleanup - Abortando inicialización...');
      abortController.abort();
    };
  }, [navigate, initAttempt]);

  useEffect(() => {
    if (!planRange) return;
    const selectedDate = new Date(`${date}T00:00:00`);
    if (!Number.isNaN(selectedDate.getTime()) && !isDateWithinPlanRange(selectedDate)) {
      const clamped = clampDateToRange(selectedDate, planRange);
      const clampedISO = formatISODate(clamped);
      if (clampedISO && clampedISO !== date) {
        setDate(clampedISO);
      }
    }
    if (typeof planRange.ninjaLimit === 'number' && planRange.ninjaLimit >= 0) {
      setMaxNinjaProfiles(planRange.ninjaLimit);
    }
  }, [planRange, isDateWithinPlanRange, date]);

  // Función auxiliar para cargar perfil del usuario
  const loadUserProfile = async (loadedProfile = null) => {
    console.log('🔄 [loadUserProfile] Iniciando carga de perfil...');
    try {
      // Si no está autenticado, no hacer nada
      if (!isAuthenticated()) {
        console.log('⚠️ [loadUserProfile] Usuario NO autenticado');
        return false;
      }

      console.log('✅ [loadUserProfile] Usuario autenticado, llamando authManager.getProfile()');
      const profileData = loadedProfile || await authManager.getProfile();
      console.log('📦 [loadUserProfile] Datos recibidos:', profileData);
       const isBackendResponse = profileData && typeof profileData === 'object' && Object.prototype.hasOwnProperty.call(profileData, 'success');
       console.log('🔍 [loadUserProfile] isBackendResponse:', isBackendResponse);

       if (isBackendResponse) {
         console.log('✅ [loadUserProfile] Es respuesta del backend');
         console.log('🔍 [CALENDARIO] profileData.subscription:', profileData.subscription);
         console.log('🔍 [CALENDARIO] ¿Es null?', profileData.subscription === null);
         console.log('🔍 [CALENDARIO] ¿Es undefined?', profileData.subscription === undefined);
         
         // Mapear subscriptionInfo de camelCase a snake_case para compatibilidad con getPlanDateLimits
         let mappedSubscription = null;
         if (profileData.subscription) {
           console.log('🔍 [CALENDARIO] subscriptionInfo ANTES del mapeo:', {
             horizonLimitDays: profileData.subscription.horizonLimitDays,
             legacyType: profileData.subscription.legacyType,
             displayName: profileData.subscription.displayName
           });
           
          mappedSubscription = {
            ...profileData.subscription,
            // Agregar campos en snake_case que getPlanDateLimits espera
            horizon_limit_days: profileData.subscription.horizonLimitDays,
            plan_type: profileData.subscription.legacyType,
            ninja_limit: profileData.subscription.ninjaLimit,
            max_ninja_profiles: profileData.subscription.ninjaLimit,
            // ✅ CRÍTICO: Agregar paid_at para cálculo de rangos
            paid_at: profileData.subscription.paidAt,
            created_at: profileData.subscription.createdAt,
            // ✅ CRÍTICO: Agregar billing_cycle para bonus 2026
            billing_cycle: profileData.subscription.billingCycle
          };
          
          console.log('🔍 [CALENDARIO] subscriptionInfo DESPUÉS del mapeo:', {
            horizon_limit_days: mappedSubscription.horizon_limit_days,
            plan_type: mappedSubscription.plan_type,
            displayName: mappedSubscription.displayName,
            paid_at: mappedSubscription.paid_at,
            created_at: mappedSubscription.created_at
          });

          // ✅ Log visual de límites Ninja
          console.log('🥷 ════════════════════════════════════════════════════════');
          console.log('🥷 LÍMITES NINJA - DESGLOSE DETALLADO');
          console.log('🥷 ════════════════════════════════════════════════════════');
          console.log('📦 RESPUESTA COMPLETA DEL BACKEND (profileData.subscription):');
          console.log(JSON.stringify(profileData.subscription, null, 2));
          console.log('📦 ¿Tiene ninjaLimits en profileData?', !!profileData.ninjaLimits);
          if (profileData.ninjaLimits) {
            console.log('📦 ninjaLimits del backend:', profileData.ninjaLimits);
          }
          console.table({
            'Total': mappedSubscription.ninjaLimit || 0,
            'Base (Plan)': mappedSubscription.ninjaLimitBase || 0,
            'Comprados': mappedSubscription.ninjaLimitPurchased || 0,
            'Manuales': mappedSubscription.ninjaLimitManual || 0
          });
          console.log('🥷 ════════════════════════════════════════════════════════');
         }
         
         setSubscriptionInfo(mappedSubscription);
         setMaxNinjaProfiles(getNinjaLimit(profileData.subscription));
         
         console.log('🎯 [loadUserProfile] hasCompletedOnboarding:', profileData.hasCompletedOnboarding);
         console.log('🎯 [loadUserProfile] hasProfile:', !!profileData.profile);
         
         if (profileData.hasCompletedOnboarding === true && profileData.profile) {
           const profile = profileData.profile;

        console.log('✅ [loadUserProfile] Cargando datos del perfil:', {
          hasUserId: !!profile.userId,
          userId: profile.userId,
          name: profile.name,
          birth: profile.birth,
          occupation: profile.occupation,
          status: profile.status
        });
        
        setUserData(profile);
        setName(profile.name || "");
        setApellidoPaterno(profile.apellidoPaterno || "");
        setApellidoMaterno(profile.apellidoMaterno || "");
        
        let formattedBirth = "";
        if (profile.birth) {
          try {
            if (profile.birth.includes('T')) {
              formattedBirth = profile.birth.split('T')[0];
            } else {
              formattedBirth = profile.birth;
            }
          } catch (e) {
            console.warn('Error formateando fecha:', e);
            formattedBirth = profile.birth;
          }
        }
        console.log('📅 [loadUserProfile] Fecha formateada:', formattedBirth);
        setBirth(formattedBirth);
        
        setRole(Array.isArray(profile.occupation) ? profile.occupation : []);
        setStatus(profile.status || "");
        console.log('💾 [loadUserProfile] Estados actualizados');
        
        setProfiles(prevProfiles => {
          const updatedProfiles = prevProfiles.map(p => {
            if (p.id === 'main') {
              return {
                ...p,
                name: profile.name || "",
                apellidoPaterno: profile.apellidoPaterno || "",
                apellidoMaterno: profile.apellidoMaterno || "",
                birth: formattedBirth,
                occupation: Array.isArray(profile.occupation) ? profile.occupation : [],
                status: profile.status || "",
                isNinja: false
              };
            }
            return p;
          });
          localStorage.setItem('numerologiaProfiles', JSON.stringify(updatedProfiles));
          return updatedProfiles;
        });
        
        console.log('✅ Perfil cargado exitosamente con preferencias');
        console.log('📅 Fecha de nacimiento:', formattedBirth);
           return true;
         }

         return false;
       }

       setSubscriptionInfo(null);

       if (profileData) {
         const demoProfile = {
           id: profileData.id || 'demo-profile',
           userId: profileData.user_id || profileData.id || 'demo-user',
           name: profileData.name || 'Usuario Demo',
           apellidoPaterno: profileData.apellido_paterno || '',
           apellidoMaterno: profileData.apellido_materno || '',
           birth: profileData.birth_date || profileData.birth || '',
           occupation: Array.isArray(profileData.occupation) ? profileData.occupation : [],
           status: profileData.status || ''
         };

         setUserData(demoProfile);
         setName(demoProfile.name || "");
         setApellidoPaterno(demoProfile.apellidoPaterno || "");
         setApellidoMaterno(demoProfile.apellidoMaterno || "");
         setBirth(demoProfile.birth);
         setRole(demoProfile.occupation);
         setStatus(demoProfile.status || "soltero/a");

         setProfiles(prevProfiles => {
           const updatedProfiles = prevProfiles.map(p => {
             if (p.id === 'main') {
               return {
                 ...p,
                 name: demoProfile.name,
                 apellidoPaterno: demoProfile.apellidoPaterno,
                 apellidoMaterno: demoProfile.apellidoMaterno,
                 birth: demoProfile.birth,
                 occupation: demoProfile.occupation,
                 status: demoProfile.status,
                 isNinja: false
               };
             }
             return p;
           });
           localStorage.setItem('numerologiaProfiles', JSON.stringify(updatedProfiles));
           return updatedProfiles;
         });

         return true;
       }

      return false;
    } catch (error) {
       console.error('❌ Error obteniendo perfil:', error);
      throw error;
    }
  };

  // 🥷 Funciones del Modo Ninja
  const handleSelectProfile = async (profile) => {
    trackActivity('profile_selected', 'profile', { profileType: profile.isNinja ? 'ninja' : 'primary' });
    setCurrentProfileId(profile.id);
    localStorage.setItem('numerologiaCurrentProfile', profile.id);
    // Activar en backend si es ninja y hay sesión
    try {
      if (isAuthenticated() && profile.isNinja) {
        await activateNinjaProfile(profile.id);
      }
    } catch (e) {
      console.warn('⚠️ No se pudo activar el perfil ninja en backend:', e.message);
    }
    
    // Actualizar estado con datos del perfil seleccionado
    setName(profile.name);
    setApellidoPaterno(profile.apellidoPaterno || '');
    setApellidoMaterno(profile.apellidoMaterno || '');
    setBirth(profile.birth);
    setRole(Array.isArray(profile.occupation) ? profile.occupation : [profile.occupation || 'otro']);
    setStatus(profile.status || 'soltero/a');
  };

  const handleAddProfile = async (newProfile) => {
    const limit = getMaxProfiles();
    const currentNinjaCount = profiles.filter(p => p.isNinja).length;
    if (limit !== undefined && limit !== null && limit !== Infinity && currentNinjaCount >= limit) {
      alert('Has alcanzado el número máximo de perfiles Ninja permitidos por tu plan.');
      return;
    }
    try {
      if (isAuthenticated()) {
        const { ninjaProfile } = await createNinjaProfile({
          name: newProfile.name,
          apellidoPaterno: newProfile.apellidoPaterno,
          apellidoMaterno: newProfile.apellidoMaterno,
          birth: newProfile.birth,
          occupation: newProfile.occupation,
          status: newProfile.status
        });
        const apiProfile = {
          id: ninjaProfile.id,
          name: ninjaProfile.name,
          apellidoPaterno: ninjaProfile.apellido_paterno || '',
          apellidoMaterno: ninjaProfile.apellido_materno || '',
          birth: ninjaProfile.birth_date?.split('T')[0] || ninjaProfile.birth_date,
          occupation: ninjaProfile.occupation || [],
          status: ninjaProfile.status || '',
          isNinja: true
        };
        const updated = [...profiles, apiProfile];
        setProfiles(updated);
        localStorage.setItem('numerologiaProfiles', JSON.stringify(updated));
        handleSelectProfile(apiProfile);
        
        // Recargar límite actualizado
        const listData = await listNinjaProfiles();
        if (typeof listData?.maxAllowed === 'number') {
          setMaxNinjaProfiles(listData.maxAllowed);
        }
        if (listData?.limits) {
          // Mapear snake_case a camelCase
          setNinjaLimits({
            base: listData.limits.base_ninjas || 0,
            manuallyAdded: listData.limits.manually_added_ninjas || 0,
            purchased: listData.limits.purchased_ninjas || 0,
            total: listData.limits.total_ninjas || 0,
            created: listData.limits.ninjas_created || 0,
            available: listData.limits.ninjas_available || 0
          });
        }
      } else {
        // Modo sin login: local solamente
        const local = { ...newProfile, id: `ninja-${Date.now()}`, isNinja: true };
        const updated = [...profiles, local];
        setProfiles(updated);
        localStorage.setItem('numerologiaProfiles', JSON.stringify(updated));
        handleSelectProfile(local);
      }
    } catch (e) {
      console.error('❌ Error creando perfil ninja:', e);
      alert('No se pudo crear el perfil ninja. Intenta de nuevo.');
    }
  };

  // Cargar perfiles ninja del backend si hay sesión
  useEffect(() => {
    (async () => {
      try {
        if (!isAuthenticated()) return;
        const data = await listNinjaProfiles();
        
        // Actualizar límite desde el backend
        if (typeof data?.maxAllowed === 'number') {
          setMaxNinjaProfiles(data.maxAllowed);
        }
        if (data?.limits) {
          // Mapear snake_case a camelCase
          setNinjaLimits({
            base: data.limits.base_ninjas || 0,
            manuallyAdded: data.limits.manually_added_ninjas || 0,
            purchased: data.limits.purchased_ninjas || 0,
            total: data.limits.total_ninjas || 0,
            created: data.limits.ninjas_created || 0,
            available: data.limits.ninjas_available || 0
          });
        }
        
        const ninjas = (data?.ninjaProfiles || []).map(n => ({
          id: n.id,
          name: n.name,
          apellidoPaterno: n.apellido_paterno || '',
          apellidoMaterno: n.apellido_materno || '',
          birth: n.birth_date?.split('T')[0] || n.birth_date,
          occupation: n.occupation || [],
          status: n.status || '',
          isNinja: true
        }));
        setProfiles(prev => {
          const main = prev.find(p => p.id === 'main') || {
            id: 'main', name: '', apellidoPaterno: '', apellidoMaterno: '', birth: '', occupation: [], status: '', isNinja: false
          };
          const combined = [main, ...ninjas];
          localStorage.setItem('numerologiaProfiles', JSON.stringify(combined));
          return combined;
        });
      } catch (e) {
        console.warn('⚠️ No se pudieron cargar perfiles ninja:', e.message);
      }
    })();
  }, []);

  // Obtener límite de perfiles según el plan (desde el backend)
  const getMaxProfiles = () => {
    // 1. Prioridad: usar ninjaLimits.total (consolidado: base + manual + comprados)
    if (ninjaLimits && typeof ninjaLimits.total === 'number') {
      return ninjaLimits.total;
    }
    // 2. Fallback: usar planRange.ninjaLimit (solo base del plan)
    if (planRange && typeof planRange.ninjaLimit === 'number') {
      return planRange.ninjaLimit;
    }
    // 3. Fallback final: usar maxNinjaProfiles (valor antiguo)
    return maxNinjaProfiles ?? 0;
  };

  const handleLogout = () => {
    authManager.logout();
    setUserData(null);
    navigate('/login');
  };


  // Función para manejar la finalización del onboarding
  const handleOnboardingComplete = async (data) => {
    // Guardar en el servicio de auth si está logueado
    if (isAuthenticated()) {
      try {
        // Guardar perfil completo incluyendo focusAreas y goals
        await authManager.saveProfile({
          name: data.name,
          apellidoPaterno: data.apellidoPaterno || '',
          apellidoMaterno: data.apellidoMaterno || '',
          birth: data.birth,
          occupation: Array.isArray(data.occupation) ? data.occupation : [data.occupation],
          status: data.status,
          focusAreas: data.focusAreas || [],
          goals: data.goals || ''
        });
        if (!await loadUserProfile()) throw new Error('No se pudo cargar tu perfil guardado. Intenta de nuevo.');
        setShowOnboarding(false);
        console.log('✅ Perfil completo guardado con preferencias');
      } catch (error) {
        console.error('Error saving profile:', error);
        throw error;
      }
    }
  };
  
  // Función para manejar clic en día del calendario
  const handleDayClick = (isoDate) => {
    if (!isDateWithinPlanRange(isoDate)) {
      if (planRange) {
        const coverage = formatRangeForHumans(planRange.start, planRange.end);
        const label = planRange.label || 'el rango disponible';
        const info = coverage ? `${label} (${coverage})` : label;
        const planName = planDisplayName || 'actual';
        alert(`⛔ Tu plan ${planName} cubre ${info}.
Actualiza tu plan para explorar más fechas.`);
      } else {
        alert('⛔ La fecha seleccionada no está disponible en tu plan actual.');
      }
      return;
    }
    
    trackActivity('calendar_day_selected', 'calendar');
    setDate(isoDate);
    setScope("day"); // Cambiar a vista diaria
    
    // Activar el loader
    setIsLoadingCards(true);
    
    // Hacer scroll INMEDIATAMENTE para que el usuario vea el loader trabajando
    setTimeout(() => {
      const energiaElement = document.getElementById('energia-del-dia');
      if (energiaElement) {
        // Obtener la posición del elemento
        const elementPosition = energiaElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px de offset desde arriba
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
    
    // Desactivar el loader después de 2 segundos
    setTimeout(() => {
      setIsLoadingCards(false);
    }, 2000);
  };

  // ✅ Sistema de mensajes migrado completamente a backendMessages
  // Las vistas semanales y mensuales usan fallbacks locales en la función build()

  // Las variables name, birth, role, status ya están declaradas como estados con useState
  // Se actualizan automáticamente en loadUserProfile() y en el useEffect de currentProfile

  const parsedDOB = useMemo(() => { 
    if (!birth || birth === "") {
      // Si no hay fecha de nacimiento, usar fecha dummy para evitar NaN
      return { y: 1990, m: 1, d: 1 }; 
    }
    const [y, m, d] = birth.split("-").map(Number); 
    // Validar que sean números válidos
    if (isNaN(y) || isNaN(m) || isNaN(d)) {
      console.error('❌ Fecha de nacimiento inválida:', birth);
      return { y: 1990, m: 1, d: 1 };
    }
    return { y, m, d }; 
  }, [birth]);
  
  const current = useMemo(() => new Date(date + "T00:00:00"), [date]);

  const nums = useMemo(() => {
    const Y = current.getFullYear(); const M = current.getMonth() + 1; const D = current.getDate();
    const UY = universalYear(Y); const UM = universalMonth(M, Y); const UD = universalDay(D, M, Y);
    const UW = universalWeek(current);
    const PY = personalYear({ d: parsedDOB.d, m: parsedDOB.m }, Y);
    const PM = personalMonth({ d: parsedDOB.d, m: parsedDOB.m }, current);
    const PD = personalDay({ d: parsedDOB.d, m: parsedDOB.m }, current);
    const PW = personalWeek({ d: parsedDOB.d, m: parsedDOB.m }, current); // Semana Personal
    const wIdx = weekIndex(current);
    return { UY, UM, UD, UW, PY, PM, PD, PW, wIdx };
  }, [current, parsedDOB]);

  const baseByScopeNumber = scope === "day" ? nums.PD : scope === "week" ? nums.PW : nums.PM;
  const base = NUM_KEYWORDS[baseByScopeNumber] || NUM_KEYWORDS[reduceNum(baseByScopeNumber)];
  const pmKey = NUM_KEYWORDS[nums.PM]?.k || "tu herramienta del mes";
  const umKey = NUM_KEYWORDS[nums.UM]?.k || "el telón universal";

  // Generar matiz de trabajo basado en las ocupaciones seleccionadas
  const roleMatizWork = (() => {
    const rolesStr = role.join(" y ");
    if (role.some(r => r.includes("empr"))) return "con enfoque emprendedor";
    if (role.some(r => r.includes("ventas"))) return "cuidando tu seguimiento";
    if (role.some(r => r.includes("creador") || r.includes("artista") || r.includes("diseñador"))) return "desde tu creatividad";
    return "en tus tareas clave";
  })();
  const loveMatiz = status.startsWith("sol") ? "abre espacio para conocer a alguien" : "acuerdos y tiempos de calidad";

  const EMO = { work: "💼✨", love: "💞🤝", health: "🌿🧘", spirit: "🔮✨" };

  function build(area) {
    // 🆕 USAR MENSAJES DEL BACKEND (solo para vista diaria)
    if (scope === 'day' && !backendMessages.loading) {
      // Mapear categorías: frontend usa 'health', backend usa 'wellness'
      const backendCategory = area === 'health' ? 'wellness' : area;
      const backendMsg = backendMessages[backendCategory];
      
      if (backendMsg) {
        return {
          text: backendMsg.text,
          avoid: backendMsg.avoid
        };
      }
    }
    
    // Fallback a mensajes generados localmente
    const v = base?.verbs?.[area] || "actúa";
    const k = base?.k || "tu mejor versión";
    const avoid = base?.caution || "sabotearte";

    const prefix = scope === "day"
      ? `Hoy ${v}`
      : scope === "week"
      ? `Esta semana ${v}`
      : `Durante este mes ${v}`;

    if (area === "work") {
      const body = scope === "day"
        ? `${roleMatizWork}. Usa ${k} como brújula. Haz 1 bloque de foco (45–90 min) y 2 microtareas de 15–25 min. Nota del mes (${nums.PM}): ${pmKey}.`
        : scope === "week"
        ? `con la energía de la Semana ${nums.wIdx} (${NUM_KEYWORDS[nums.PW]?.k || "energía semanal"}). Aterriza un entregable, define métricas simples y celebra cada avance. Recuerda que el Mes Universal ${nums.UM} impulsa ${umKey}.`
        : `alineado a tu Mes Personal ${nums.PM} (${pmKey}). Traza 3 hitos con fecha y un sistema de seguimiento liviano; negocia desde tu centro. (Mes Universal ${nums.UM}: ${umKey}).`;
      return { text: `${EMO.work} ${prefix} ${body}`, avoid };
    }

    if (area === "love") {
      const body = scope === "day"
        ? `desde ${k}. Propón un momento de conexión auténtica; escucha antes de responder. Si estás soltero/a, ${loveMatiz}.`
        : scope === "week"
        ? `creando pequeños rituales de cuidado. Una conversación honesta + un plan simple juntos. Semana ${nums.wIdx}: ${NUM_KEYWORDS[nums.PW]?.k || "energía semanal"}.`
        : `cuidando acuerdos claros y muestras constantes de aprecio. Mes Personal ${nums.PM}: ${pmKey}.`;
      return { text: `${EMO.love} ${prefix} ${body}`, avoid };
    }

    if (area === "health") {
      const body = scope === "day"
        ? `cuidando tu bienestar. 10–15 min de movimiento consciente y buena hidratación. Deja 5 min para respiración antes de dormir.`
        : scope === "week"
        ? `con una rutina amable: 3 sesiones cortas, alimentación simple y descanso digital por las noches. Semana ${nums.wIdx}: ${NUM_KEYWORDS[nums.PW]?.k || "energía semanal"}.`
        : `instalando hábitos base (sueño, agua, movimiento). Alinea tu energía a ${pmKey}.`;
      return { text: `${EMO.health} ${prefix} ${body}`, avoid };
    }

    const body = scope === "day"
      ? `Respira profundo y conecta con ${k}. Cierra el día con 3 agradecimientos y una afirmación breve.`
      : scope === "week"
      ? `Dedica 10 min diarios a silencio o journaling. Observa señales y sincronías; integra lo aprendido.`
      : `Define una intención matriz y revísala cada domingo. Mes Universal ${nums.UM}: ${umKey}.`;
    return { text: `${EMO.spirit} ${prefix}. ${body}`, avoid };
  }

  // 🎯 USAR MENSAJES DEL BACKEND si están disponibles (scope === 'day')
  const hasBackendMessages = scope === 'day' && backendMessages.work && backendMessages.love && backendMessages.wellness && backendMessages.spiritual;
  const isLoadingBackend = scope === 'day' && backendMessages.loading;
  
  const msgs = hasBackendMessages
    ? {
        work: backendMessages.work,
        love: backendMessages.love,
        health: backendMessages.wellness,  // wellness → health mapping
        spirit: backendMessages.spiritual
      }
    : isLoadingBackend
    ? {
        // Mientras carga, mostrar placeholder para evitar "parpadeo" de cambio
        work: { text: '⏳ Generando tu mensaje personalizado...', avoid: 'Espera un momento' },
        love: { text: '⏳ Generando tu mensaje personalizado...', avoid: 'Espera un momento' },
        health: { text: '⏳ Generando tu mensaje personalizado...', avoid: 'Espera un momento' },
        spirit: { text: '⏳ Generando tu mensaje personalizado...', avoid: 'Espera un momento' }
      }
    : {
        work: build("work"),
        love: build("love"),
        health: build("health"),
        spirit: build("spirit")
      };

  const headerTitle = scope === "day" ? `Mensaje Diario` : scope === "week" ? `Mensaje Semanal (Semana ${nums.wIdx})` : `Mensaje Mensual`;
  const headlineNumber = baseByScopeNumber;
  const headlineDesc = scope === "day" ? "Enfoque del día" : scope === "week" ? (NUM_KEYWORDS[nums.PW]?.k || "energía semanal") : "Herramienta del mes";

  const energyChips = (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
      <HeadChip label="Año Personal / Año Universal" value={`${nums.PY} / ${nums.UY}`} desc="aprendizaje / telón colectivo" tooltipType="year-binomial" />
      <HeadChip label="Mes Personal / Mes Universal" value={`${nums.PM} / ${nums.UM}`} desc="herramienta del mes / vibración del mes" tooltipType="month-binomial" />
      {scope !== "month" && (
        <HeadChip label={scope === "day" ? `Semana Personal / Semana Universal` : `Semana Personal / Semana Universal`} value={`${nums.PW} / ${nums.UW}`} desc={`${NUM_KEYWORDS[nums.PW]?.k || "energía semanal"} / ${NUM_KEYWORDS[nums.UW]?.k || "energía universal"}`} tooltipType="week-binomial" />
      )}
      {scope === "day" && <HeadChip label="Día Personal / Día Universal" value={`${nums.PD} / ${nums.UD}`} desc="enfoque del día / clima del día" tooltipType="day-binomial" />}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // 🆕 CARGA DE MENSAJES DEL BACKEND (Optimizado con dependencias mínimas)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Key única para determinar cuándo recargar mensajes
  // Solo cambia cuando: fecha, perfil activo, o vista cambian
  const messagesReloadKey = useMemo(() => {
    if (!currentProfile?.birth) return null;
    return `${date}_${currentProfile.id}_${currentProfile.birth}_${scope}`;
  }, [date, currentProfile?.id, currentProfile?.birth, scope]);
  
  // Ref para AbortController de mensajes
  const messagesAbortRef = useRef(null);
  
  useEffect(() => {
    // No cargar si la app no está lista o no hay key válida
    if (!isAppReady || !messagesReloadKey) {
      return;
    }
    
    // Solo cargar para vista diaria
    if (scope !== 'day') {
      setBackendMessages(prev => ({ ...prev, loading: false }));
      return;
    }
    
    // Verificar que tenemos datos del perfil
    if (!currentProfile?.birth || !currentProfile?.name) {
      console.log('⏳ [loadBackendMessages] Esperando datos del perfil...');
      return;
    }
    
    console.log('🔄 [useEffect loadBackendMessages] Ejecutando...', {
      messagesReloadKey,
      currentProfileId: currentProfile?.id,
      currentProfileName: currentProfile?.name
    });
    
    // Cancelar solicitud anterior si existe
    if (messagesAbortRef.current) {
      messagesAbortRef.current.abort();
    }
    messagesAbortRef.current = new AbortController();
    const abortSignal = messagesAbortRef.current.signal;
    
    async function loadBackendMessages() {
      // ✅ LIMPIAR mensajes viejos antes de cargar nuevos
      setBackendMessages({
        work: null,
        love: null,
        wellness: null,
        spiritual: null,
        loading: true,
        error: null
      });
      
      try {
        const categories = ['work', 'love', 'wellness', 'spiritual'];
        
        // Usar datos directamente del currentProfile (fuente de verdad)
        const profileBirth = currentProfile.birth;
        const [, m, d] = profileBirth.split("-").map(Number);
        const targetDate = new Date(`${date}T00:00:00`);
        const PD = personalDay({ d, m }, targetDate);
        const UD = universalDay(targetDate.getDate(), targetDate.getMonth() + 1, targetDate.getFullYear());
        
        const numerologyData = {
          personalDay: PD,
          universalDay: UD,
          date: date
        };
        
        // Determinar profileId
        let profileId;
        if (currentProfile.isNinja) {
          if (currentProfile.id && !String(currentProfile.id).startsWith('ninja-')) {
            profileId = currentProfile.id;
          } else {
            const ninjaKey = `${currentProfile.name}_${currentProfile.birth}`;
            profileId = getOrCreateNinjaProfileId(ninjaKey);
          }
        } else if (userData?.id) {
          profileId = userData.id;
        } else if (userData?.profileId) {
          profileId = userData.profileId;
        } else {
          profileId = getOrCreateProfileId();
        }
        
        const userDataForAPI = {
          name: currentProfile.name,
          birth: currentProfile.birth,
          occupation: currentProfile.occupation || [],
          status: currentProfile.status || '',
          profileId: profileId,
          isNinja: currentProfile.isNinja || false
        };
        
        // Verificar si fue abortado antes de hacer las llamadas
        if (abortSignal.aborted) return;
        
        // Generar los 4 mensajes en paralelo
        const promises = categories.map(category =>
          generatePersonalizedMessage(category, numerologyData, userDataForAPI)
            .then(message => ({ category, message, success: true }))
            .catch(error => {
              console.error(`Error en ${category}:`, error);
              return { category, message: null, success: false, error };
            })
        );
        
        const results = await Promise.all(promises);
        
        // Verificar si fue abortado después de las llamadas
        if (abortSignal.aborted) return;
        
        // Parsear cada mensaje
        const newMessages = {};
        results.forEach(({ category, message, success }) => {
          if (success && message) {
            const parts = message.split('⚠️ Evita:');
            newMessages[category] = {
              text: parts[0].trim(),
              avoid: parts[1] ? parts[1].trim() : 'No disponible',
              cached: message.cached || false
            };
          } else {
            newMessages[category] = null;
          }
        });
        
        console.log('✅ Mensajes cargados:', newMessages);
        
        if (!abortSignal.aborted) {
          setBackendMessages({
            ...newMessages,
            loading: false,
            error: null
          });
        }
        
      } catch (error) {
        if (!abortSignal.aborted) {
          console.error('❌ Error cargando mensajes del backend:', error);
          setBackendMessages(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }));
        }
      }
    }
    
    loadBackendMessages();
    
    // Cleanup
    return () => {
      if (messagesAbortRef.current) {
        messagesAbortRef.current.abort();
      }
    };
  }, [messagesReloadKey, isAppReady, scope, currentProfile, userData, date]);

  const resolvePinnacleTarget = useCallback(() => {
    if (!currentProfile) {
      return { reason: "Selecciona un perfil para continuar." };
    }

    if (!currentProfile.birth) {
      return { reason: "Completa la fecha de nacimiento para calcular el Pináculo." };
    }

    if (!isAuthenticated()) {
      return { reason: "Inicia sesión para visualizar esta sección." };
    }

    const profileType = currentProfile.isNinja ? "ninja" : "primary";

    if (profileType === "primary") {
      const profileId = userData?.id || userData?.profileId || userData?.userId;
      if (!profileId) {
        return { reason: "No encontramos un perfil principal sincronizado con tu cuenta. Completa tu onboarding para habilitar esta sección." };
      }
      return { profileType, profileId };
    }

    const profileId = currentProfile.id;
    if (!profileId || String(profileId).startsWith("ninja-")) {
      return { reason: "Este perfil Ninja solo existe de forma local. Guárdalo en tu cuenta para ver su Pináculo." };
    }

    return { profileType, profileId };
  }, [currentProfile, userData]);

  const handleRefreshPinnacle = useCallback(() => {
    setPinnacleRefreshKey(prev => prev + 1);
  }, []);

  // Cargar datos del Pináculo cuando corresponda
  useEffect(() => {
    if (currentView !== "pinaculo" && currentView !== "horizontal") {
      return;
    }

    const target = resolvePinnacleTarget();
    if (target?.reason) {
      setPinnacleState({
        loading: false,
        data: null,
        error: null,
        reason: target.reason
      });
      return;
    }

    let cancelled = false;
    setPinnacleState(prev => ({
      ...prev,
      loading: true,
      error: null,
      reason: null
    }));

    fetchOrCreatePinnacle(target)
      .then(record => {
        if (cancelled) return;
        setPinnacleState({
          loading: false,
          data: record,
          error: null,
          reason: null
        });
      })
      .catch(error => {
        if (cancelled) return;
        const message = error?.status === 401
          ? "Tu sesión expiró. Inicia sesión nuevamente."
          : error?.message || "No pudimos cargar tu Pináculo en este momento.";
        setPinnacleState({
          loading: false,
          data: null,
          error: message,
          reason: null
        });
      });

    return () => {
      cancelled = true;
    };
  }, [currentView, resolvePinnacleTarget, pinnacleRefreshKey]);

  // Ya no usamos currentScreen porque las rutas las maneja React Router
  // Este componente solo renderiza la app principal cuando el usuario ya está autenticado
  
  // NO HACER EARLY RETURN - Viola regla de hooks
  // En su lugar, renderizar condicionalmente al final

  const formatBillingCycleLabel = (cycle) => {
    if (!cycle) return '';
    const normalized = String(cycle).toLowerCase();
    if (normalized === 'monthly') return 'Cobro mensual';
    if (normalized === 'annual') return 'Cobro anual';
    if (normalized === 'legacy') return 'Plan legacy';
    return cycle;
  };

  const planDisplayName = useMemo(() => {
    if (!subscriptionInfo) return '';
    if (subscriptionInfo.displayName) return subscriptionInfo.displayName;
    if (subscriptionInfo.planCode) return subscriptionInfo.planCode.replace(/_/g, ' ');
    return 'Plan Numerathum';
  }, [subscriptionInfo]);

  const planPriceLabel = useMemo(() => {
    if (!subscriptionInfo || typeof subscriptionInfo.priceMxn !== 'number') return '';
    const suffix = subscriptionInfo.billingCycle === 'monthly' ? '/mes' : subscriptionInfo.billingCycle === 'annual' ? '/año' : '';
    return `${formatCurrency(subscriptionInfo.priceMxn)} ${suffix}`.trim();
  }, [subscriptionInfo]);

  const planBillingLabel = useMemo(() => subscriptionInfo?.isTrial ? 'Prueba gratuita' : formatBillingCycleLabel(subscriptionInfo?.billingCycle), [subscriptionInfo]);

  const promoLabel = useMemo(() => {
    if (!subscriptionInfo?.promoApplied) return '';
    const expires = subscriptionInfo.promoExpiresAt ? new Date(subscriptionInfo.promoExpiresAt).toLocaleDateString('es-MX') : null;
    return expires ? `Promo activa hasta ${expires}` : 'Promo activa';
  }, [subscriptionInfo]);

  // Función para manejar el date picker (DESPUÉS de declarar todos los estados y funciones que usa)
  const handleDatePickerChange = useCallback((e) => {
    const selectedDate = e.target.value; // formato YYYY-MM-DD
    if (selectedDate && isDateWithinPlanRange(selectedDate)) {
      setDate(selectedDate);
      setScope("day");
      // Scroll a las cards
      setTimeout(() => {
        const cardsSection = document.getElementById("mensajes-diarios");
        if (cardsSection) {
          cardsSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else if (selectedDate) {
      const coverage = planRange ? formatRangeForHumans(planRange.start, planRange.end) : '';
      const planName = planDisplayName || 'actual';
      alert(`⛔ La fecha seleccionada no está disponible en tu plan ${planName}.${coverage ? `\nTu plan cubre: ${coverage}` : ''}`);
    }
  }, [isDateWithinPlanRange, planRange, planDisplayName, setDate, setScope]);

  // Días importantes del mes para exportación
  // Si hay suscripción activa, incluye TODOS los días importantes del mes (sin filtro de rango)
  const importantDaysForCurrentMonth = useMemo(() => {
    if (!parsedDOB || !parsedDOB.d || !parsedDOB.m) return [];
    const result = [];
    const year = current.getFullYear();
    const month = current.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateObj = new Date(year, month, day);
      // Si hay suscripción activa, NO filtrar por rango - permitir todo el mes
      if (!subscriptionInfo && !isDateWithinPlanRange(dateObj)) continue;
      const pd = personalDay({ d: parsedDOB.d, m: parsedDOB.m }, dateObj);
      const info = IMPORTANT_DAYS[pd];
      if (info) {
        result.push({ day, personalDay: pd, info });
      }
    }
    return result;
  }, [current, parsedDOB, isDateWithinPlanRange, subscriptionInfo]);

  // Años disponibles para exportación
  // Si hay suscripción activa, mostrar año actual y siguiente
  // Si hay planRange, incluir también los años del rango
  const exportYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = new Set();
    
    // Siempre incluir año actual y siguiente si hay suscripción
    if (subscriptionInfo) {
      years.add(currentYear);
      years.add(currentYear + 1);
    }
    
    // Agregar años del rango del plan si existe
    if (planRange && planRange.start && planRange.end) {
      const startYear = planRange.start.getFullYear();
      const endYear = planRange.end.getFullYear();
      for (let y = startYear; y <= endYear; y += 1) {
        years.add(y);
      }
    }
    
    return Array.from(years).sort((a, b) => a - b);
  }, [planRange, subscriptionInfo]);

  // Sincronizar exportYear cuando cambia el scope o los años disponibles
  useEffect(() => {
    if (exportScope !== 'year') return;
    if (!exportYears.length) return;
    if (exportYears.includes(exportYear)) return;
    // Si el año actual no está disponible, usar el año visible del calendario o el primero disponible
    const visibleYear = calendarMonth.getFullYear();
    const next = exportYears.includes(visibleYear) ? visibleYear : exportYears[0];
    setExportYear(next);
  }, [exportScope, exportYear, exportYears, calendarMonth]);

  // Días importantes para el año seleccionado (exportación anual)
  // Si hay suscripción activa, incluye TODOS los días importantes del año (sin filtro de rango)
  const importantDaysForSelectedYear = useMemo(() => {
    if (!Number.isFinite(exportYear)) return [];
    const start = new Date(exportYear, 0, 1);
    const end = new Date(exportYear, 11, 31);
    const result = [];
    if (!parsedDOB || !parsedDOB.d || !parsedDOB.m) return [];
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      // Si hay suscripción activa, NO filtrar por rango - permitir todo el año
      if (!subscriptionInfo && !isDateWithinPlanRange(d)) continue;
      const pd = personalDay({ d: parsedDOB.d, m: parsedDOB.m }, d);
      const info = IMPORTANT_DAYS[pd];
      if (info) {
        result.push({ date: new Date(d), personalDay: pd, info });
      }
    }
    
    return result;
  }, [exportYear, parsedDOB, isDateWithinPlanRange, subscriptionInfo]);

  // Si hay suscripción activa, permitir exportar si hay días importantes
  // Ya no dependemos de planRange porque quitamos el filtro de rango para suscripciones activas
  const canExportCalendar = Boolean(
    subscriptionInfo && 
    (exportScope === 'month'
      ? importantDaysForCurrentMonth.length
      : importantDaysForSelectedYear.length)
  );
  const calendarName = useMemo(() => (subscriptionInfo ? `Numerathum - ${planDisplayName}` : 'Numerathum - Días Importantes'), [subscriptionInfo, planDisplayName]);

  const handleExportPlanCalendar = useCallback(() => {
    if (!subscriptionInfo) {
      alert('No pudimos detectar tu plan actual.');
      return;
    }
    if (!canExportCalendar) {
      alert(exportScope === 'year'
        ? `No hay días importantes dentro del rango permitido en ${exportYear}.`
        : 'No hay días importantes dentro de la cobertura de tu plan este mes.');
      return;
    }

    trackActivity('calendar_export_requested', 'calendar', { scope: exportScope });
    if (exportScope === 'year') {
      exportToCalendar(importantDaysForSelectedYear, new Date(exportYear, 0, 1), { plan: subscriptionInfo }, {
        calendarName: `${calendarName} - ${exportYear}`,
        calendarDescription: planRangeSummary || undefined,
        planRange,
        filename: `numerologia-${exportYear}.ics`
      });
    } else {
      exportToCalendar(importantDaysForCurrentMonth, current, { plan: subscriptionInfo }, {
        calendarName,
        calendarDescription: planRangeSummary || undefined,
        planRange
      });
    }
  }, [
    subscriptionInfo,
    canExportCalendar,
    exportScope,
    importantDaysForCurrentMonth,
    importantDaysForSelectedYear,
    current,
    calendarName,
    planRangeSummary,
    planRange,
    exportYear
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 LOADING SCREEN - Bloquea render hasta que el backend responda
  // ═══════════════════════════════════════════════════════════════════════════
  if (!isAppReady) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "rgb(var(--color-morado-rgb) / 0.05)" }}>
        <div className="text-center p-8">
          {/* Logo animado */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <BrandLogo className="w-64 mx-auto" />
          </motion.div>
          
          {/* Título */}
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-2xl font-bold text-morado mb-2"
          >
            Numerathum
          </motion.h1>
          
          {/* Subtítulo */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-gray-600 mb-6"
          >
            Mi Brújula del Tiempo
          </motion.p>
          
          {/* Spinner */}
          {!initError && <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-4"
          >
            <div className="w-12 h-12 mx-auto border-4 border-[#d4af37] border-t-morado rounded-full animate-spin" />
          </motion.div>}
          
          {/* Mensaje de carga */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-sm text-morado font-medium"
          >
            {initLoadingMessage}
          </motion.p>
          
          {/* Error si existe */}
          {initError && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl"
              role="alert"
            >
              <p className="text-red-700 text-sm">{initError}</p>
              <button
                onClick={() => setInitAttempt(attempt => attempt + 1)}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition"
              >
                Reintentar
              </button>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // Renderizado condicional: Onboarding o App principal
  if (showOnboarding) {
    return <><TrialBanner trial={trialInfo} /><Onboarding onComplete={handleOnboardingComplete} /><WelcomeTour key={getCurrentUser()?.id || 'guest'} userId={getCurrentUser()?.id} /></>;
  }

  // Vistas del menú; las que tienen `soon` se muestran deshabilitadas con la cápsula "Próximamente",
  // y `badge` añade una cápsula con ese texto sin deshabilitar la pestaña
  const navItems = [
    { view: "energia", label: "ENERGÍA DIARIA" },
    { view: "etapa", label: "ETAPA DE VIDA" },
    { view: "pinaculo", label: "MI PINÁCULO" },
    { view: "agenda", label: "📚 AGENDA 2026" },
    { view: "agenda2027", label: "📚 AGENDA 2027", soon: true },
    { view: "pareja", label: "NUMEROLOGÍA DE PAREJA", badge: "💜 Regalo" },
    { view: "horizontal", label: "🧭 QUIÉN SOY" },
  ];

  // Escritorio: la cápsula flota sobre el borde superior de la pestaña sin cambiar su alto,
  // por eso el contenedor no lleva overflow ni padding extra (recortaría o estiraría las pestañas).
  const navStrip = (
    <nav aria-label="Vistas" className="inline-flex items-stretch rounded-lg border border-[#d4af37] bg-white">
      {navItems.map(({ view, label, soon, badge }, i) => {
        const shape = `${i > 0 ? "border-l border-[#d4af37]/40" : "rounded-l-[7px]"} ${i === navItems.length - 1 ? "rounded-r-[7px]" : ""}`;
        const base = `relative flex items-center whitespace-nowrap px-[13px] py-2.5 text-[13px] font-semibold uppercase ${shape}`;
        const capsule = text => (
          <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-[60%] whitespace-nowrap rounded-full bg-[#d4af37] px-2.5 py-0.5 text-[10px] font-bold normal-case leading-tight text-morado shadow pointer-events-none">
            {text}
          </span>
        );
        if (soon) {
          return (
            <button
              key={view}
              type="button"
              disabled
              title="Agenda 2027: próximamente"
              className={`${base} bg-white text-morado cursor-not-allowed appearance-none disabled:opacity-100`}
              style={{ WebkitTextFillColor: "currentColor" }}
            >
              {capsule("Próximamente")}
              {label}
            </button>
          );
        }
        return (
          <button
            key={view}
            type="button"
            onClick={() => setCurrentView(view)}
            aria-current={currentView === view ? "page" : undefined}
            className={`${base} transition-colors ${currentView === view ? "bg-morado text-white" : "text-morado hover:bg-morado/5"}`}
          >
            {badge && capsule(badge)}
            {label}
          </button>
        );
      })}
    </nav>
  );

  // Móvil y tablet (< lg, la barra no cabe): menú hamburguesa con las vistas y las acciones de la cuenta; la cápsula va dentro de la fila
  const mobileRow = "flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold";
  const mobileMenu = (
    <nav
      id="menu-movil"
      aria-label="Menú"
      className={`${mobileMenuOpen ? "block" : "hidden"} lg:hidden mt-3 overflow-hidden rounded-[10px] border border-[#d4af37] bg-white divide-y divide-[#d4af37]/40`}
    >
      {navItems.map(({ view, label, soon, badge }) =>
        soon ? (
          <div key={view} className={`${mobileRow} uppercase text-morado/70`} aria-disabled="true">
            {label}
            <span className="ml-auto whitespace-nowrap rounded-full bg-[#d4af37] px-2.5 py-0.5 text-[10px] font-bold normal-case leading-tight text-morado">
              Próximamente
            </span>
          </div>
        ) : (
          <button
            key={view}
            type="button"
            onClick={() => { setCurrentView(view); setMobileMenuOpen(false); }}
            aria-current={currentView === view ? "page" : undefined}
            className={`${mobileRow} uppercase ${currentView === view ? "bg-morado text-white" : "text-morado hover:bg-morado/5"}`}
          >
            {label}
            {badge && (
              <span className="ml-auto whitespace-nowrap rounded-full bg-[#d4af37] px-2.5 py-0.5 text-[10px] font-bold normal-case leading-tight text-morado">
                {badge}
              </span>
            )}
          </button>
        )
      )}
      {isAuthenticated() && (
        <>
          <button
            type="button"
            onClick={() => { setMobileMenuOpen(false); setWelcomeTourRequest(value => value + 1); }}
            className={`${mobileRow} text-morado hover:bg-morado/5`}
          >
            <HelpCircle size={16} aria-hidden="true" /> Ver beneficios
          </button>
          <button
            type="button"
            onClick={() => { setMobileMenuOpen(false); trialInfo?.isActive ? navigate('/trial/status') : setShowSubscriptionManager(true); }}
            className={`${mobileRow} text-morado hover:bg-morado/5`}
          >
            <Settings size={16} aria-hidden="true" /> Mi cuenta
          </button>
          <button
            type="button"
            onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
            className={`${mobileRow} text-red-600 hover:bg-red-50`}
          >
            <LogOut size={16} aria-hidden="true" /> Salir
          </button>
        </>
      )}
    </nav>
  );

  return (
    <>
      <SEOHead 
        title="Mi Calendario - Numerathum"
        description="Tu calendario numerológico personal"
        noindex={true}
      />
      <div className="min-h-screen" style={{ background: "rgb(var(--color-morado-rgb) / 0.05)" }}>
        <TrialBanner trial={trialInfo} />
        {/* Header responsive */}
      <header className="sticky top-0 z-20 backdrop-blur bg-white/80 border-b" style={{ borderColor: "#d4af37" }}>
        <div className="max-w-6xl mx-auto px-3 md:px-4 py-3">
          {/* Logo y título */}
          <div className="flex items-center justify-between">
            <Link
              to="/app"
              onClick={() => setCurrentView("energia")}
              aria-label="Ir a Energía diaria"
              className="flex items-center gap-2 md:gap-3 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d4af37]"
            >
              <BrandLogo className="w-36 sm:w-48" />
            </Link>
            <div className="flex items-center gap-2">
              {/* Selector de Perfiles Ninja - Siempre visible */}
              <div className="mr-1">
                <ProfileSelector
                  profiles={profiles}
                  currentProfile={currentProfile}
                  onSelectProfile={handleSelectProfile}
                  onAddProfile={handleAddProfile}
                onUpdateProfile={async (payload) => {
                  try {
                    if (isAuthenticated()) {
                      const { ninjaProfile } = await updateNinjaProfile(payload);
                      const apiProfile = {
                        id: ninjaProfile.id,
                        name: ninjaProfile.name,
                        apellidoPaterno: ninjaProfile.apellido_paterno || '',
                        apellidoMaterno: ninjaProfile.apellido_materno || '',
                        birth: ninjaProfile.birth_date?.split('T')[0] || ninjaProfile.birth_date,
                        occupation: ninjaProfile.occupation || [],
                        status: ninjaProfile.status || '',
                        isNinja: true
                      };
                      setProfiles(prev => {
                        const updated = prev.map(p => p.id === apiProfile.id ? apiProfile : p);
                        localStorage.setItem('numerologiaProfiles', JSON.stringify(updated));
                        return updated;
                      });
                      if (currentProfileId === apiProfile.id) handleSelectProfile(apiProfile);
                    }
                  } catch (e) {
                    console.error('❌ Error actualizando ninja:', e);
                    alert('No se pudo actualizar el perfil');
                  }
                }}
                onDeleteProfile={async (profile) => {
                  try {
                    if (isAuthenticated() && profile.isNinja) {
                      await deleteNinjaProfile(profile.id);
                    }
                    setProfiles(prev => {
                      const arr = prev.filter(p => p.id !== profile.id);
                      localStorage.setItem('numerologiaProfiles', JSON.stringify(arr));
                      return arr;
                    });
                    if (currentProfileId === profile.id) {
                      setCurrentProfileId('main');
                      localStorage.setItem('numerologiaCurrentProfile', 'main');
                    }
                  } catch (e) {
                    console.error('❌ Error eliminando ninja:', e);
                    alert('No se pudo eliminar el perfil');
                  }
                }}
                  maxProfiles={getMaxProfiles()}
                  onPurchaseMore={() => setShowPurchaseModal(true)}
                  ninjaLimits={ninjaLimits}
                />
              </div>
              
              {/* En móvil y tablet estas acciones viven dentro del menú hamburguesa */}
              {isAuthenticated() && (
                <>
                  <button
                    type="button"
                    onClick={() => setWelcomeTourRequest(value => value + 1)}
                    className="hidden lg:inline-flex items-center gap-1.5 px-2 md:px-3 py-1.5 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm border border-[#d4af37] text-morado hover:bg-morado/5"
                    aria-label="Ver beneficios"
                    title="Ver beneficios"
                  >
                    <HelpCircle size={16} aria-hidden="true" />
                    <span className="hidden sm:inline">Ver beneficios</span>
                  </button>
                  <button 
                    onClick={() => trialInfo?.isActive ? navigate('/trial/status') : setShowSubscriptionManager(true)}
                    className="hidden lg:inline-flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm border"
                    style={{ borderColor: "#d4af37", color: "var(--color-morado)" }}
                    title="Gestionar suscripción"
                  >
                    <Settings size={14} className="md:w-4 md:h-4" />
                    <span className="hidden sm:inline">Mi Cuenta</span>
                    <span className="sm:hidden">⚙️</span>
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="hidden lg:inline-flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm border border-red-300 text-red-600 hover:bg-red-50"
                    title="Cerrar sesión"
                  >
                    <LogOut size={14} className="md:w-4 md:h-4" />
                    <span className="hidden sm:inline">Salir</span>
                    <span className="sm:hidden">🚪</span>
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(open => !open)}
                aria-expanded={mobileMenuOpen}
                aria-controls="menu-movil"
                aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
                className="lg:hidden grid h-[38px] w-[38px] place-items-center rounded-lg border border-[#d4af37] bg-white text-morado"
              >
                <span className="relative block h-3 w-[18px]" aria-hidden="true">
                  <span className={`absolute inset-x-0 top-0 h-0.5 rounded bg-current transition-transform duration-200 ${mobileMenuOpen ? "translate-y-[5px] rotate-45" : ""}`} />
                  <span className={`absolute inset-x-0 top-[5px] h-0.5 rounded bg-current transition-opacity duration-200 ${mobileMenuOpen ? "opacity-0" : ""}`} />
                  <span className={`absolute inset-x-0 top-[10px] h-0.5 rounded bg-current transition-transform duration-200 ${mobileMenuOpen ? "-translate-y-[5px] -rotate-45" : ""}`} />
                </span>
              </button>
            </div>
          </div>
          {mobileMenu}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 md:px-4 py-4 md:py-6">
        {/* Controles */}
        {/* Botones de navegación de vista (en móvil y tablet están en el menú hamburguesa) */}
        <div className="hidden lg:flex items-center gap-3 mb-6">
          {navStrip}
        </div>
        {/* Menú flotante inferior: aparece al hacer scroll hacia abajo (solo escritorio; en móvil y tablet el header fijo trae el menú) */}
        <div
          className={`fixed inset-x-0 bottom-0 z-40 hidden lg:flex justify-center px-3 pb-3 pt-3 pointer-events-none transition-all duration-300 ${showBottomNav ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}
          aria-hidden={!showBottomNav}
        >
          <div className="pointer-events-auto shadow-2xl rounded-lg">
            {navStrip}
          </div>
        </div>
        {currentView === "energia" && (
          <>
            {/* Perfil compacto y (opcional) editor */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-4 md:p-6 border mb-6" style={{ borderColor: "#d4af37" }}>
          <div className="space-y-4">
            {/* Header con saludo, date picker y botón HOY */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-morado mb-1">
                  Hola, {name}! 👋
                  {currentProfile.isNinja && (
                    <span className="text-lg ml-2">🥷</span>
                  )}
                </h1>
                <div className="w-16 h-0.5 bg-morado rounded-full"></div>
              </div>
              
              {/* Controles de fecha */}
              <div className="flex items-center gap-2">
                {/* Date picker */}
                <div className="relative">
                  <input
                    type="date"
                    value={date}
                    onChange={handleDatePickerChange}
                    min={planRange?.start ? formatISODate(planRange.start) : undefined}
                    max={planRange?.end ? formatISODate(planRange.end) : undefined}
                    className="px-3 py-2 text-sm font-medium text-morado bg-white border border-[#d4af37] rounded-lg hover:bg-morado/5 focus:outline-none focus:ring-2 focus:ring-[#d4af37] transition-colors duration-200 shadow-sm cursor-pointer"
                    title="Selecciona una fecha"
                  />
                </div>
                
                {/* Botón HOY */}
                <button
                  onClick={() => {
                    const today = new Date();
                    const y = today.getFullYear();
                    const m = String(today.getMonth() + 1).padStart(2, "0");
                    const d = String(today.getDate()).padStart(2, "0");
                    setDate(`${y}-${m}-${d}`);
                  }}
                  className="px-4 py-2 text-sm font-semibold text-morado bg-white border border-[#d4af37] rounded-lg hover:bg-morado/5 transition-colors duration-200 shadow-sm"
                >
                  HOY
                </button>
              </div>
            </div>

            {/* Información del usuario */}
            <div className="text-sm text-gray-600">
              {name} {apellidoPaterno} {apellidoMaterno} • {role.join(", ")} • {status}
            </div>

            {/* Energía del día y fecha */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-morado font-extrabold bg-white border-2 shadow-sm" style={{ borderColor: "#d4af37" }}>
                  {scope === "day" ? `${nums.PD}/${nums.UD}` : scope === "week" ? `${nums.PW}/${nums.UW}` : `${nums.PM}/${nums.UM}`}
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wide text-morado font-semibold">
                    {scope === "day" ? "ENERGÍA DEL DÍA" : scope === "week" ? "ENERGÍA SEMANAL" : "ENERGÍA MENSUAL"}
                  </div>
                  <div className="text-sm text-gray-700">
                    {scope === "day" ? `${NUM_KEYWORDS[nums.PD]?.k || "enfoque"} / ${NUM_KEYWORDS[nums.UD]?.k || "clima"}` : 
                     scope === "week" ? `${NUM_KEYWORDS[nums.PW]?.k || "energía semanal"} / ${NUM_KEYWORDS[nums.UW]?.k || "energía universal"}` : 
                     `${NUM_KEYWORDS[nums.PM]?.k || "herramienta"} / ${NUM_KEYWORDS[nums.UM]?.k || "vibración"}`}
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-lg md:text-xl font-bold text-morado">
                  {current.toLocaleDateString('es-ES', { 
                    month: 'long', 
                    year: 'numeric',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chips de energías según alcance */}
        <section className="mb-6">{energyChips}</section>

        {/* Tarjetas de áreas */}
        <section id="mensajes-diarios" className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Bonus (si aplica) */}
          <div className="md:col-span-2">
            {(() => {
              const specials = computeSpecials({ d: parsedDOB.d, m: parsedDOB.m }, current);
              return (
                <BonusCard 
                  specials={specials} 
                  pd={personalDay({ d: parsedDOB.d, m: parsedDOB.m }, current)} 
                  pm={personalMonth({ d: parsedDOB.d, m: parsedDOB.m }, current)} 
                  um={universalMonth(current.getMonth()+1, current.getFullYear())} 
                  ud={universalDay(current.getDate(), current.getMonth()+1, current.getFullYear())}
                />
              );
            })()}
          </div>


          <AreaCard title="Trabajo / Proyecto" icon={Briefcase} gradient="var(--color-morado)" message={msgs.work.text} caution={msgs.work.avoid} isLoading={isLoadingCards} pd={nums.PD} ud={nums.UD} />
          <AreaCard title="Amor / Vínculos" icon={Heart} gradient="var(--color-morado)" message={msgs.love.text} caution={msgs.love.avoid} isLoading={isLoadingCards} pd={nums.PD} ud={nums.UD} />
          <AreaCard title="Bienestar" icon={Activity} gradient="var(--color-morado)" message={msgs.health.text} caution={msgs.health.avoid} isLoading={isLoadingCards} pd={nums.PD} ud={nums.UD} />
          <AreaCard title="Espiritual" icon={Sparkles} gradient="var(--color-morado)" message={msgs.spirit.text} caution={msgs.spirit.avoid} isLoading={isLoadingCards} pd={nums.PD} ud={nums.UD} />
        </section>

        {/* Calendario mensual (debajo de las cards) */}
        <section className="mt-8">
          <CalendarExplanation />
          {/* Calendario ultra-compacto para móviles muy pequeños */}
          <div className="block xs:hidden">
            <CalendarExplanation isMobile={true} />
            <CompactMobileCalendar
              dob={{ d: parsedDOB.d, m: parsedDOB.m }}
              currentDate={calendarMonth}
              selectedISO={date}
              onPick={handleDayClick}
              isDateAllowed={isDateWithinPlanRange}
              rangeSummary={planRange ? planRangeSummary : ''}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              canPrevMonth={canNavigatePrevMonth()}
              canNextMonth={canNavigateNextMonth()}
            />
            <ImportantDaysExplanation currentMonth={calendarMonth} dob={{ d: parsedDOB.d, m: parsedDOB.m }} />
          </div>
          
          {/* Calendario móvil optimizado */}
          <div className="hidden xs:block sm:hidden">
            <CalendarExplanation isMobile={true} />
            <MobileCalendar
              dob={{ d: parsedDOB.d, m: parsedDOB.m }}
              currentDate={calendarMonth}
              selectedISO={date}
              onPick={handleDayClick}
              isDateAllowed={isDateWithinPlanRange}
              rangeSummary={planRange ? planRangeSummary : ''}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              canPrevMonth={canNavigatePrevMonth()}
              canNextMonth={canNavigateNextMonth()}
            />
            <ImportantDaysExplanation currentMonth={calendarMonth} dob={{ d: parsedDOB.d, m: parsedDOB.m }} />
          </div>
          
          {/* Calendario desktop */}
          <div className="hidden sm:block">
            <CalendarMonth
              dob={{ d: parsedDOB.d, m: parsedDOB.m }}
              currentDate={calendarMonth}
              selectedISO={date}
              onPick={handleDayClick}
              isDateAllowed={isDateWithinPlanRange}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              canPrevMonth={canNavigatePrevMonth()}
              canNextMonth={canNavigateNextMonth()}
            />
            <ImportantDaysExplanation currentMonth={calendarMonth} dob={{ d: parsedDOB.d, m: parsedDOB.m }} />
          </div>

        </section>

        {/* Actividades ideales para hoy */}
        {scope === "day" && parsedDOB && (
          <section className="mt-8">
            <ActivitySuggestions
              universalDay={universalDay(current.getDate(), current.getMonth() + 1, current.getFullYear())}
              personalDay={personalDay({ d: parsedDOB.d, m: parsedDOB.m }, current)}
              universalMonth={universalMonth(current.getMonth() + 1, current.getFullYear())}
              universalWeek={universalWeek(current)}
              isExpanded={true}
            />
          </section>
        )}

        {/* Información del plan - ÚLTIMA SECCIÓN */}
        {subscriptionInfo && (
          <section className="mt-8 p-4 bg-white/70 backdrop-blur-sm rounded-2xl border border-[#d4af37]/60 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="space-y-1 text-sm">
              <div className="text-xs uppercase text-gray-500 font-semibold tracking-wide">Tu plan</div>
              <div className="text-lg font-bold text-morado">{planDisplayName}</div>
              {planRangeSummary && (
                <div className="text-xs text-gray-500">Cobertura: {planRangeSummary}</div>
              )}
              <div className="text-xs text-gray-500">Perfiles Ninja: {ninjaCount} / {getMaxProfiles()}</div>
              {planBillingLabel && <div className="text-xs text-gray-500">{planBillingLabel}</div>}
              {promoLabel && (
                <div className="text-xs text-morado font-semibold">{promoLabel}</div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:text-right text-sm">
              <div className="space-y-1 sm:text-left md:text-right">
                {planPriceLabel && <div className="font-semibold text-morado">{planPriceLabel}</div>}
              </div>
              <div className="flex flex-col items-stretch gap-1">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <select
                    value={exportScope}
                    onChange={(e) => setExportScope(e.target.value)}
                    className="px-3 py-2 rounded-xl border bg-white text-xs font-semibold text-morado hover:bg-morado/5 transition"
                    style={{ borderColor: "#d4af37" }}
                    aria-label="Rango de exportación"
                  >
                    <option value="month">Mes visible</option>
                    <option value="year">Año completo</option>
                  </select>
                  {exportScope === 'year' && (
                    <select
                      value={exportYear}
                      onChange={(e) => setExportYear(Number(e.target.value))}
                      className="px-3 py-2 rounded-xl border bg-white text-xs font-semibold text-morado hover:bg-morado/5 transition"
                      style={{ borderColor: "#d4af37" }}
                      aria-label="Año a exportar"
                    >
                      {(exportYears.length ? exportYears : [exportYear]).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex items-stretch gap-2">
                  <button
                    type="button"
                    onClick={handleExportPlanCalendar}
                    disabled={!canExportCalendar}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${
                      canExportCalendar
                        ? 'bg-morado text-white hover:shadow-lg'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <CalendarIcon className="w-4 h-4" />
                    Exportar .ICS
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCalendarHelpModal(true)}
                    className="inline-flex items-center justify-center w-10 rounded-xl border bg-white hover:bg-morado/5 transition"
                    style={{ borderColor: "#d4af37" }}
                    title="¿Cómo integrar a mi calendario?"
                    aria-label="¿Cómo integrar a mi calendario?"
                  >
                    <HelpCircle className="w-4 h-4 text-morado" />
                  </button>
                </div>
                {!canExportCalendar && (
                  <span className="text-[10px] text-gray-500 text-center">
                    {exportScope === 'year'
                      ? 'No hay días especiales dentro del rango permitido en ese año.'
                      : 'No hay días especiales dentro del rango visible este mes.'}
                  </span>
                )}
              </div>
            </div>
          </section>
        )}
          </>
        )}

        {currentView === "etapa" && (
          <div className="space-y-6">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-4 md:p-6 border" style={{ borderColor: "#d4af37" }}>
              <div className="text-center">
                <h1 className="text-2xl md:text-3xl font-bold text-morado mb-2">
                  Tu Etapa de Vida Actual
                </h1>
                <div className="w-24 h-0.5 bg-morado rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">
                  {name} {apellidoPaterno} {apellidoMaterno} • {role.join(", ")} • {status}
                </p>
              </div>
            </div>
            <LifeStagesCard birthDate={birth} currentDate={current} />
          </div>
        )}

        {currentView === "pinaculo" && (
          <PinnacleCard
            profileName={currentProfile.name || (currentProfile.isNinja ? "Perfil Ninja" : "Tu perfil")}
            birthDate={currentProfile.birth}
            data={pinnacleState.data}
            loading={pinnacleState.loading}
            error={pinnacleState.error}
            reason={pinnacleState.reason}
            onRetry={handleRefreshPinnacle}
          />
        )}

        {currentView === "agenda" && (
          <AgendaDownloadSection />
        )}

        {currentView === "pareja" && (
          <>
            <GiftCouponCard />
            <SynastrySection currentProfile={currentProfile} />
          </>
        )}

        {currentView === "horizontal" && (
          <HorizontalNumerologicaSection
            profileName={currentProfile.name || (currentProfile.isNinja ? "Perfil Ninja" : "Tu perfil")}
            birthDate={currentProfile.birth}
            data={pinnacleState.data}
            loading={pinnacleState.loading}
            error={pinnacleState.error}
            reason={pinnacleState.reason}
            onRetry={handleRefreshPinnacle}
          />
        )}
      </main>

              <footer className="py-8 text-center text-xs text-gray-600">Hecho con 💜 y ✨ — Numerathum: Mi Brújula del Tiempo</footer>
        
        {/* Modal de gestión de suscripción */}
        <WelcomeTour key={getCurrentUser()?.id || 'guest'} userId={getCurrentUser()?.id} requestVersion={welcomeTourRequest} />
        <LauraRecommendsToast />
        {showSubscriptionManager && (
          <SubscriptionManager 
            onClose={() => setShowSubscriptionManager(false)}
            subscriptionInfo={subscriptionInfo}
            ninjaProfiles={profiles.filter(p => p.isNinja)}
          />
        )}

        {/* Modal de ayuda para integrar calendario */}
        <AnimatePresence>
          {showCalendarHelpModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
              onClick={() => setShowCalendarHelpModal(false)}
            >
              <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border"
                style={{ borderColor: "#d4af37" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="px-5 py-4 text-white flex items-center justify-between"
                  style={{ background: "var(--color-morado)" }}
                >
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    <div>
                      <div className="font-semibold leading-tight">Integrar a mi calendario</div>
                      <div className="text-xs opacity-90 leading-tight">Google Calendar, Apple Calendar u Outlook</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCalendarHelpModal(false)}
                    className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 transition flex items-center justify-center"
                    aria-label="Cerrar"
                    title="Cerrar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-5 space-y-4 text-sm text-gray-700">
                  <div className="rounded-xl border p-4 bg-morado/5" style={{ borderColor: "#d4af37" }}>
                    <div className="font-semibold text-morado mb-1">1) Descarga el archivo</div>
                    <div>
                      Elige <span className="font-semibold">Mes visible</span> o <span className="font-semibold">Año completo</span> (y el año) y luego presiona <span className="font-semibold">"Exportar .ICS"</span>.
                      Se descargará un archivo con tus <span className="font-semibold">Días Importantes</span>.
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-morado">2) Google Calendar (recomendado)</div>
                    <ol className="list-decimal pl-5 space-y-1">
                      <li>Abre Google Calendar en tu computadora.</li>
                      <li>Ve a <span className="font-semibold">Configuración</span> → <span className="font-semibold">Importar y exportar</span>.</li>
                      <li>En <span className="font-semibold">Importar</span>, elige el archivo <span className="font-semibold">.ics</span> que descargaste.</li>
                      <li>Selecciona el calendario destino (puedes crear uno llamado <span className="font-semibold">"Numerathum"</span>).</li>
                      <li>Importa y listo: verás los eventos en tus fechas.</li>
                    </ol>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-morado">3) Apple Calendar (iPhone / Mac)</div>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Abre el archivo <span className="font-semibold">.ics</span> desde Archivos / Mail y confirma agregar eventos.</li>
                      <li>En Mac, también puedes abrir el archivo con la app Calendario.</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-morado">4) Outlook</div>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Importa el <span className="font-semibold">.ics</span> desde la sección de Calendario (Importar).</li>
                    </ul>
                  </div>

                  <div className="text-xs text-gray-500">
                    Tip: si vuelves a exportar e importar el mismo mes, el archivo usa identificadores estables para reducir duplicados.
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowCalendarHelpModal(false)}
                      className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
                      style={{ background: "var(--color-morado)" }}
                    >
                      Entendido
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal de compra de perfiles Ninja adicionales */}
        <NinjaPurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          currentLimits={ninjaLimits}
        />

      </div>
    </>
    );
  }
